import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Button, Dropdown } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { saveLineStatus, saveComment } from '../../context/CommonContex';
import HoverPO from '../HoverPO';
import OrderCommentsButton from '../comments/OrderCommentsButton';
import { notifyOrderHistoryChanged } from '../orderHistory/useOrderHistoryUnread';
import CellPresence from '../Common/CellPresence';
import PresenceBar from '../Common/PresenceBar';
import { usePresenceContext } from '../../context/PresenceContext';
import { getCommentThreadKey, useCommentDock } from '../../context/CommentDockContext';
import { useNotifications } from '../../context/NotificationsContext';
import { cellKey, parseCellKey } from '../../utils/presenceKeys';
import './OrderPopup.css';
import useShippingInstructions from '../StickyNotes/useShippingInstructions';
import useStickyNotes from '../StickyNotes/useStickyNotes';
import OrderNotesPanel from './OrderNotesPanel';
import BomAvailabilityPill from './BomAvailabilityPill';

// Line Status Tags with colors (display order)
const LINE_STATUS_TAGS = [
  { label: 'Released', color: '#02f77c' },
  { label: 'Shipped', color: '#2D895F' },
  { label: 'In Kitting', color: '#CB5722' },
  { label: 'In Process', color: '#9288FF' },
  { label: 'In Testing', color: '#84E6F8' },
  { label: 'In Outgoing Inspection', color: '#B8B2E1' },
  { label: 'Not Released', color: '#F3DC6B' },
  { label: 'On Hold Cart', color: '#C56A00' },
  { label: 'Staging Cart', color: '#1DA1DB' },
  { label: 'Waiting On Components', color: '#A40202' },
  { label: 'PMA', color: '#594E9F' },
  { label: 'RMA', color: '#EB4F4F' },
  { label: 'ENG Review', color: '#625C5C' },
  { label: 'Charge', color: '#1d545e' }
];

/** Display quantities as whole numbers with no decimal point (e.g. 5000.000000000 → 5000). */
const formatQtyDisplay = (value) => {
  if (value == null || value === '') return '0';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return String(Math.trunc(num));
};

const getBomPartQtyAvail = (bom) => {
  const part = bom?.part || {};
  return (Number(part.qoh_cache) || 0) - (Number(part.qr_cache) || 0);
};

/** BOM details under Action: negative QTY AVAIL rows first. */
const sortBomPartsNegativeQtyAvailFirst = (bomParts) => {
  if (!Array.isArray(bomParts) || bomParts.length === 0) return [];
  return [...bomParts].sort((a, b) => {
    const qtyA = getBomPartQtyAvail(a);
    const qtyB = getBomPartQtyAvail(b);
    const aNegative = qtyA < 0;
    const bNegative = qtyB < 0;
    if (aNegative !== bNegative) {
      return aNegative ? -1 : 1;
    }
    return qtyA - qtyB;
  });
};

// Order Status Tags with colors (display order)
const ORDER_STATUS_TAGS = [
  { label: 'CC Hold', color: '#E39827' },
  { label: 'Wire Hold', color: '#E39827' },
  { label: 'Credit Hold', color: '#E39827' },
  { label: 'PPA', color: '#E39827' },
  { label: 'Paid', color: '#28a745' },
  { label: 'Partial', color: '#EF9884' },
  { label: 'CoT Required', color: '#4AB2D5' },
  { label: 'PMA', color: '#4AB2D5' },
  { label: 'AS9102', color: '#4AB2D5' },
  { label: 'Burn Test', color: '#4AB2D5' },
  { label: 'No Overage', color: '#BD83B8' },
  { label: 'Specific Lengths Required', color: '#BD83B8' },
  { label: 'Do Not Insure', color: '#BD83B8' },
  { label: 'No Charge', color: '#BD83B8' },
  { label: 'Hot Order/AOG', color: '#FF6B35', icon: 'fa fa-fire' },
  { label: 'PO Review', color: '#ffc400' }
];

const isInShippingOrShippedLabel = (raw) => {
  const s = String(
    typeof raw === 'string'
      ? raw
      : raw?.label || raw?.status || raw?.name || ''
  ).trim().toLowerCase();
  return s === 'in shipping' || s === 'ready to ship' || s === 'shipped';
};

const isStaggingCartLabel = (raw) => {
  const s = String(
    typeof raw === 'string'
      ? raw
      : raw?.label || raw?.status || raw?.name || ''
  ).trim().toLowerCase();
  return s === 'staging cart' || s === 'stagging cart';
};

const isOnHoldCartLabel = (raw) => {
  const s = String(
    typeof raw === 'string'
      ? raw
      : raw?.label || raw?.status || raw?.name || ''
  ).trim().toLowerCase();
  return s === 'on hold cart';
};

const formatLineStatusLabel = (raw) => {
  const label = String(
    typeof raw === 'string'
      ? raw
      : raw?.label || raw?.status || raw?.name || ''
  ).trim();
  if (isStaggingCartLabel(label)) return 'Staging Cart';
  return label;
};

const lineStatusLabelsEqual = (a, b) => {
  const la = String(typeof a === 'string' ? a : a?.label || '').trim().toLowerCase();
  const lb = String(typeof b === 'string' ? b : b?.label || '').trim().toLowerCase();
  const normalize = (s) => (s === 'stagging cart' ? 'staging cart' : s);
  return normalize(la) === normalize(lb);
};

const lineStatusKey = (id) => String(id);

const currentPresenceEmail = () =>
  (localStorage.getItem('userEmail') || localStorage.getItem('email') || '').trim().toLowerCase();

/** Same cell as inline: orders:line:{lineId}:lineStatus, from the live roster. */
const readLineStatusPresence = (roster, page, lineId, me) => {
  if (lineId == null || lineId === '' || !roster) return { users: [], owner: null };

  const idStr = String(lineId);
  const keys = new Set();
  if (page) keys.add(cellKey(page, 'line', lineId, 'lineStatus'));
  keys.add(cellKey('orders', 'line', lineId, 'lineStatus'));
  keys.add(cellKey('orders-internal', 'line', lineId, 'lineStatus'));

  Object.keys({ ...(roster.cells || {}), ...(roster.locks || {}) }).forEach((key) => {
    const parsed = parseCellKey(key);
    if (parsed.field === 'lineStatus' && String(parsed.entityId) === idStr) {
      keys.add(key);
    }
  });

  for (const key of keys) {
    const users = (roster.cells?.[key] || []).filter((user) => user.email !== me);
    const lock = roster.locks?.[key];
    const owner = lock && lock.email !== me ? lock : null;
    if (users.length || owner) return { users, owner };
  }

  return { users: [], owner: null };
};

/** Everyone currently working this order: details modal, open row, or a line status. */
const readOrderModalPresence = (roster, page, orderNum, meEmail, lineIds = []) => {
  if (!orderNum || !roster) return { users: [], cells: {} };

  const idStr = String(orderNum);
  const lineIdSet = new Set((lineIds || []).map(String).filter(Boolean));
  const keys = new Set();
  const pages = [page, 'orders', 'orders-internal', 'order-validation'].filter(Boolean);

  pages.forEach((p) => {
    keys.add(cellKey(p, 'order', orderNum, 'modal'));
    keys.add(cellKey(p, 'order', orderNum, 'row'));
  });

  Object.keys({ ...(roster.cells || {}), ...(roster.locks || {}) }).forEach((key) => {
    const parsed = parseCellKey(key);
    const entityId = String(parsed.entityId);
    const onThisOrder =
      (parsed.entityType === 'order' && entityId === idStr && (parsed.field === 'modal' || parsed.field === 'row'))
      || (parsed.field === 'lineStatus' && lineIdSet.has(entityId));
    if (onThisOrder) keys.add(key);
  });

  const cells = {};
  const byEmail = new Map();
  keys.forEach((key) => {
    const inCell = roster.cells?.[key] || [];
    if (inCell.length) cells[key] = inCell;
    inCell.forEach((user) => {
      if (user?.email && !byEmail.has(user.email)) byEmail.set(user.email, user);
    });
  });

  const me = (roster.here || []).find((user) => user.email === meEmail);
  if (me && !byEmail.has(me.email)) byEmail.set(me.email, me);

  return { users: Array.from(byEmail.values()), cells };
};

const LineStatusPresence = ({ lineId }) => {
  const ctx = usePresenceContext();
  const me = useMemo(currentPresenceEmail, []);
  const { users, owner } = useMemo(
    () => readLineStatusPresence(ctx?.roster, ctx?.page, lineId, me),
    [ctx?.roster, ctx?.page, lineId, me]
  );
  return <CellPresence users={users} owner={owner} compact activity="on this line status" />;
};

const UPS_SHIPPING_LINK = 'https://www.ups.com/ship/basic/shipment-info?tx=20678219171053728&loc=en_US&promoCodeAlias=sPArk&WT.srch=1';
const FEDEX_SHIPPING_LINK = 'https://www.fedex.com/secure-login/en-us/#/credentials';

const renderShippingMethodLink = (methodText) => {
  const text = methodText || 'N/A';
  const lower = String(text).toLowerCase();
  if (lower.includes('fedex')) {
    return <a href={FEDEX_SHIPPING_LINK} target="_blank" rel="noopener noreferrer">{text}</a>;
  }
  if (lower.includes('ups')) {
    return <a href={UPS_SHIPPING_LINK} target="_blank" rel="noopener noreferrer">{text}</a>;
  }
  return text;
};

/** Matches Orders table “Availability Status” (missing BOM parts count). */
const computeAvailabilityReportFromOrderLines = (orderLines, excludedParts = {}) => {
  if (!Array.isArray(orderLines) || orderLines.length === 0) return null;

  const bomPartMap = new Map();
  orderLines.forEach((item) => {
    const lineShipQty = Number(item?.balancedue) || 0;
    const bomParts = item?.bom?.bomParts || [];

    bomParts.forEach((bom) => {
      const key = bom?.prcpart;
      if (!key) return;

      const part = bom?.part || {};
      const qohBom = Number(part?.qoh_cache) || 0;
      const qrBom = Number(part?.qr_cache) || 0;

      if (!bomPartMap.has(key)) {
        bomPartMap.set(key, {
          prcpart: key,
          qtyAvail: qohBom - qrBom,
        });
      }
    });
  });

  let missingPartsCount = 0;
  bomPartMap.forEach((bomPart) => {
    if (bomPart.qtyAvail < 0 && !excludedParts[bomPart.prcpart]) {
      missingPartsCount += 1;
    }
  });

  return { missingPartsCount, totalPartsCount: bomPartMap.size };
};

const resolveLineFolderLink = (line, orderAccessLinks) => {
  if (!line) return null;

  const rowLinks = orderAccessLinks?.[line.id];
  const rowLinkUrl = Array.isArray(rowLinks) && rowLinks.length > 0
    ? rowLinks[rowLinks.length - 1]?.url?.trim()
    : null;
  const namedUrls = line?.order?.orderUrls;
  const firstOrderUrl = Array.isArray(namedUrls) && namedUrls.length > 0
    ? namedUrls[0]?.url?.trim()
    : null;
  const fallbackUrl = typeof line.folderUrl === 'string' ? line.folderUrl.trim() : null;

  return rowLinkUrl || firstOrderUrl || fallbackUrl || null;
};

const EMPTY_EXCLUDED_PARTS = Object.freeze({});

const OrderPopup = ({ 
  show, 
  onClose, 
  orderData, 
  orderAccessLinks = {},
  getOrderFolderLink,
  sourceLine = null,
  commentsMap = {},
  onAddComment,
  onDeleteComment,
  onResolveThread,
  /** Which grid opened this modal, so the chat matches that page's comment thread. */
  commentSourcePage = 'orders',
  onOpenOrderAccess,
  onDeleteOrderAccessLink,
  excludedAvailabilityPartsByOrder = EMPTY_EXCLUDED_PARTS,
  onToggleExcludedAvailabilityPart,
  onArchiveBomPart,
  /** Live presence (optional). Same helpers InlineOrderDropdown uses for Line Status. */
  renderLinePresence,
  lineStatusLockOwner,
  presenceFocus,
  presenceBlur,
  lineStatusCell,
  /** Grid column state. When present, adds in this modal update that column. */
  stickyNotes: stickyNotesProp = null,
  shippingInstructions: shippingInstructionsProp = null,
}) => {
  const presenceCtx = usePresenceContext();
  const presenceMe = useMemo(currentPresenceEmail, []);
  const modalOrderRecord = (Array.isArray(orderData) && orderData[0]?.order) || sourceLine?.order || null;
  const modalOrderNumber = modalOrderRecord?.ordernum ? String(modalOrderRecord.ordernum) : '';
  const localShippingInstructions = useShippingInstructions(
    (shippingInstructionsProp || !modalOrderRecord) ? [] : [modalOrderRecord]
  );
  const localStickyNotes = useStickyNotes(
    (stickyNotesProp || !modalOrderNumber) ? [] : [modalOrderNumber]
  );
  const modalShippingInstructions = shippingInstructionsProp || localShippingInstructions;
  const modalStickyNotes = stickyNotesProp || localStickyNotes;
  const [expandedRows, setExpandedRows] = useState({});
  const [activeView, setActiveView] = useState('orderLines'); // 'orderLines', 'bomParts', or 'both'
  const [pendingExcludedPartsForOrder, setPendingExcludedPartsForOrder] = useState(EMPTY_EXCLUDED_PARTS);

  // Line Status dropdown state
  const [lineStatusSelections, setLineStatusSelections] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [lineStatusDropdownPositions, setLineStatusDropdownPositions] = useState({});
  const lineStatusTriggerRefs = useRef({});

  // Bulk line status update state
  const [bulkLineStatusDropdownOpen, setBulkLineStatusDropdownOpen] = useState(false);
  const [bulkLineStatusDropdownPosition, setBulkLineStatusDropdownPosition] = useState(null);
  const bulkLineStatusTriggerRef = useRef(null);
  const [pendingBulkTag, setPendingBulkTag] = useState(null); // Track pending bulk tag selection
  
  // Bulk mode checkbox state
  const [bulkModeEnabled, setBulkModeEnabled] = useState(false);
  const [hasBulkChanges, setHasBulkChanges] = useState(false);
  const [isSubmittingBulk, setIsSubmittingBulk] = useState(false);

  // Order Status dropdown state
  const [orderStatusSelections, setOrderStatusSelections] = useState({});
  const [submittedOrderStatuses, setSubmittedOrderStatuses] = useState({});
  const [tagsToDeselect, setTagsToDeselect] = useState({});
  const [orderStatusDropdownOpen, setOrderStatusDropdownOpen] = useState(false);
  const [orderStatusDropdownPosition, setOrderStatusDropdownPosition] = useState(null);
  const orderStatusDropdownRef = useRef(null);
  const orderStatusBoxRef = useRef(null);

  const orderFolderLink = useMemo(() => {
    const linesToCheck = [];

    if (sourceLine) {
      linesToCheck.push(sourceLine);
    }
    if (Array.isArray(orderData) && orderData.length > 0) {
      linesToCheck.push(...orderData);
    }

    if (linesToCheck.length === 0) {
      return null;
    }

    const firstLine = orderData?.[0] || sourceLine;
    const orderNum = firstLine?.order?.ordernum || sourceLine?.order?.ordernum;
    if (typeof getOrderFolderLink === 'function' && orderNum) {
      const fromParent = getOrderFolderLink(orderNum);
      if (fromParent) {
        return fromParent;
      }
    }

    // Match the same folder-link resolution used in the main Order# column icon.
    for (const line of linesToCheck) {
      const folderLink = resolveLineFolderLink(line, orderAccessLinks);
      if (folderLink) {
        return folderLink;
      }
    }

    const orderLevelFallback = typeof firstLine?.order?.folderUrl === 'string'
      ? firstLine.order.folderUrl.trim()
      : null;
    return orderLevelFallback || null;
  }, [orderData, orderAccessLinks, getOrderFolderLink, sourceLine]);

  const handleOrderFolderClick = useCallback((event) => {
    if (!orderFolderLink) {
      event.preventDefault();
    }
  }, [orderFolderLink]);

  const orderFolderTitle = orderFolderLink ? `Open folder: ${orderFolderLink}` : 'No folder link saved yet';
  const modalOrderNumberLabel = useMemo(() => {
    const firstLine = orderData?.[0];
    const orderNum = firstLine?.order?.ordernum || firstLine?.ordernum || '';
    const partialName = firstLine?.name || firstLine?.order?.name || '';
    if (orderNum && partialName) return `${orderNum}(${partialName})`;
    return orderNum || partialName || 'N/A';
  }, [orderData]);

  const headerShippingMethod = orderData?.[0]?.order?.shipmentMethod?.methodOfShipment || 'N/A';
  const headerCountryName = orderData?.[0]?.order?.country?.country_Name || 'N/A';
  const headerIsInternational = headerCountryName !== 'United States' && headerCountryName !== 'N/A';

  const modalOrderKey = useMemo(() => {
    const firstLine = orderData?.[0] || sourceLine;
    const orderNum = firstLine?.order?.ordernum || firstLine?.ordernum || '';
    return String(orderNum || '');
  }, [orderData, sourceLine]);

  const excludedPartsForOrder = useMemo(() => {
    if (!modalOrderKey) {
      return EMPTY_EXCLUDED_PARTS;
    }
    return (
      excludedAvailabilityPartsByOrder?.[modalOrderKey] ||
      excludedAvailabilityPartsByOrder?.[String(modalOrderKey)] ||
      EMPTY_EXCLUDED_PARTS
    );
  }, [modalOrderKey, excludedAvailabilityPartsByOrder]);

  const headerAvailabilityReport = useMemo(
    () => computeAvailabilityReportFromOrderLines(orderData, excludedPartsForOrder),
    [orderData, excludedPartsForOrder]
  );

  useEffect(() => {
    if (!modalOrderKey) return;

    setPendingExcludedPartsForOrder(
      excludedAvailabilityPartsByOrder?.[modalOrderKey] ||
        excludedAvailabilityPartsByOrder?.[String(modalOrderKey)] ||
        EMPTY_EXCLUDED_PARTS
    );
  }, [modalOrderKey, excludedAvailabilityPartsByOrder]);

  const renderExcludeCell = (prcpart, qtyAvail) => {
    if (Number(qtyAvail) >= 0) {
      return <span className="text-muted">-</span>;
    }

    const isPendingExcluded = !!pendingExcludedPartsForOrder[prcpart];
    const isSavedExcluded = !!excludedPartsForOrder[prcpart];
    const saveDisabled = !onToggleExcludedAvailabilityPart || isSavedExcluded === isPendingExcluded;

    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ gap: '8px', pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="btn btn-sm"
          onClick={(e) => {
            e.stopPropagation();
            if (!prcpart) return;
            setPendingExcludedPartsForOrder((prev) => ({
              ...prev,
              [prcpart]: !prev[prcpart],
            }));
          }}
          title="Select/deselect exclusion"
          style={{
            width: '16px',
            height: '16px',
            minWidth: '16px',
            padding: 0,
            borderRadius: '50%',
            border: '1px solid #0d6efd',
            backgroundColor: isPendingExcluded ? '#0d6efd' : 'white',
            lineHeight: 1,
            fontSize: '10px',
            color: 'transparent',
            pointerEvents: 'auto',
          }}
        >
          &nbsp;
        </button>
        <button
          type="button"
          className="btn btn-outline-success btn-sm"
          style={{
            minWidth: '34px',
            height: '20px',
            padding: '0 6px',
            lineHeight: 1,
            fontSize: '10px',
            cursor: saveDisabled ? 'not-allowed' : 'pointer',
            pointerEvents: 'auto',
          }}
          title="Save exclusion"
          disabled={saveDisabled}
          onClick={(e) => {
            e.stopPropagation();
            if (!onToggleExcludedAvailabilityPart || !modalOrderKey || !prcpart) return;
            onToggleExcludedAvailabilityPart(
              modalOrderKey,
              prcpart,
              !!pendingExcludedPartsForOrder[prcpart]
            );
          }}
        >
          Save
        </button>
      </div>
    );
  };

  const toggleRow = useCallback((index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);


  // Handle Line Status tag selection
  // const handleLineStatusToggle = useCallback((lineId, tag) => {
  //   setLineStatusSelections(prev => {
  //     const currentSelections = prev[lineId] || [];
  //     const isSelected = currentSelections.some(selection => selection.label === tag.label);

  //     if (isSelected) {
  //       // Remove the tag if it's already selected
  //       return {
  //         ...prev,
  //         [lineId]: currentSelections.filter(selection => selection.label !== tag.label)
  //       };
  //     } else {
  //       // Add the tag if it's not selected
  //       return {
  //         ...prev,
  //         [lineId]: [...currentSelections, tag]
  //       };
  //     }
  //   });
  // }, []);

  // Modify the toggle function to close when clicking the same dropdown
  const toggleLineStatusDropdown = useCallback((lineId) => {
    setDropdownOpen(prev => {
      // Close any other open dropdowns first
      const hasOtherOpen = Object.keys(prev).some(id => id !== lineId && prev[id]);
      
      // If this dropdown is already open, close it
      if (prev[lineId]) {
        setLineStatusDropdownPositions({});
        return {};
      }
      
      // If other dropdowns are open, close them and open this one
      if (hasOtherOpen) {
        return { [lineId]: true };
      }
      
      // Otherwise, just open this dropdown
      return { [lineId]: true };
    });
  }, []);

  // Position line-status tag menus in viewport (portal) so table overflow does not clip them
  useEffect(() => {
    const calculateLineStatusPositions = () => {
      const newPositions = {};
      Object.keys(dropdownOpen).forEach((lineId) => {
        if (dropdownOpen[lineId] && lineStatusTriggerRefs.current[lineId]) {
          const trigger = lineStatusTriggerRefs.current[lineId];
          const rect = trigger.getBoundingClientRect();
          const menuMax = 200;
          const gutter = 8;
          const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - gutter);
          const spaceAbove = Math.max(0, rect.top - gutter);
          const openAbove = spaceBelow < menuMax && spaceAbove > spaceBelow;
          const maxHeight = Math.min(menuMax, Math.max(80, openAbove ? spaceAbove : spaceBelow));
          let top = openAbove ? rect.top - maxHeight - 2 : rect.bottom + 2;
          const bottomLimit = window.innerHeight - gutter;
          if (top + maxHeight > bottomLimit) top = Math.max(gutter, bottomLimit - maxHeight);
          if (top < gutter) top = gutter;

          newPositions[lineId] = {
            top,
            left: rect.left,
            width: rect.width,
            maxHeight,
            openAbove
          };
        }
      });
      setLineStatusDropdownPositions(newPositions);
    };

    if (Object.keys(dropdownOpen).length === 0) {
      setLineStatusDropdownPositions({});
      return undefined;
    }

    const rafId = requestAnimationFrame(calculateLineStatusPositions);
    window.addEventListener('resize', calculateLineStatusPositions);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', calculateLineStatusPositions);
    };
  }, [dropdownOpen]);

  // And add this to automatically close other dropdowns when opening a new one
  useEffect(() => {
    function handleClickOutside(event) {
      const isLineStatusDropdown = event.target.closest('.line-status-dropdown');
      const isLineStatusDropdownMenu = event.target.closest('.line-status-dropdown-menu');
      const isBulkTrigger = bulkLineStatusTriggerRef.current && bulkLineStatusTriggerRef.current.contains(event.target);
      const isBulkDropdown = event.target.closest('.bulk-line-status-dropdown-menu');

      if (!isLineStatusDropdown && !isLineStatusDropdownMenu) {
        setDropdownOpen({});
      }
      
      if (!isBulkTrigger && !isBulkDropdown && bulkLineStatusDropdownOpen) {
        setBulkLineStatusDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [bulkLineStatusDropdownOpen]);

  // State to track the ID, part ID, and button position/style for the open dropdown
  const [openDropdownData, setOpenDropdownData] = useState({ id: null, partId: null, style: {} });

  const closePartLinkDropdown = useCallback(() => {
    setOpenDropdownData({ id: null, partId: null, style: {} });
  }, []);

  // Close line status dropdowns on scroll (but not when scrolling inside the menu)
  useEffect(() => {
    const handleScroll = (event) => {
      const target = event?.target;
      const isLineStatusDropdownScroll = target?.closest?.('.line-status-dropdown-menu');
      const isBulkDropdownScroll = target?.closest?.('.bulk-line-status-dropdown-menu');
      const isOrderStatusDropdownScroll = target?.closest?.('.order-status-dropdown-menu');

      if (isLineStatusDropdownScroll || isBulkDropdownScroll || isOrderStatusDropdownScroll) {
        return;
      }

      // Close all open line status dropdowns
      if (Object.keys(dropdownOpen).length > 0) {
        setDropdownOpen({});
      }
      // Close bulk dropdown on scroll
      if (bulkLineStatusDropdownOpen) {
        setBulkLineStatusDropdownOpen(false);
      }
      // Close order status dropdown on scroll (not when scrolling inside its menu)
      if (orderStatusDropdownOpen) {
        setOrderStatusDropdownOpen(false);
        setOrderStatusDropdownPosition(null);
      }
      // Close part link dropdown on scroll
      if (openDropdownData.id) {
        closePartLinkDropdown();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [dropdownOpen, bulkLineStatusDropdownOpen, orderStatusDropdownOpen, openDropdownData.id, closePartLinkDropdown]);

  // Same cell-focus rule as inline: announce Line Status presence only while a
  // line-status dropdown is actually open in a visible modal Order Lines view.
  const openLineStatusId = useMemo(
    () => Object.keys(dropdownOpen).find((id) => dropdownOpen[id]) || null,
    [dropdownOpen]
  );

  useEffect(() => {
    if (show && activeView === 'orderLines') return undefined;
    setDropdownOpen((prev) => (Object.keys(prev).some((id) => prev[id]) ? {} : prev));
    setLineStatusDropdownPositions((prev) => (Object.keys(prev).length ? {} : prev));
    return undefined;
  }, [show, activeView]);

  useEffect(() => {
    const focus = presenceFocus || presenceCtx?.reportFocus;
    const blur = presenceBlur || presenceCtx?.reportBlur;
    const page = presenceCtx?.page || 'orders';
    const keyFor = lineStatusCell || ((id) => cellKey(page, 'line', id, 'lineStatus'));
    if (!show || activeView !== 'orderLines' || !openLineStatusId || !focus || !blur) {
      return undefined;
    }
    const key = keyFor(openLineStatusId);
    focus(key);
    return () => blur(key);
  }, [
    show,
    activeView,
    openLineStatusId,
    lineStatusCell,
    presenceFocus,
    presenceBlur,
    presenceCtx?.reportFocus,
    presenceCtx?.reportBlur,
    presenceCtx?.page,
  ]);

  // Announce that this user has the Order Details modal open for this order.
  useEffect(() => {
    const focus = presenceFocus || presenceCtx?.reportFocus;
    const blur = presenceBlur || presenceCtx?.reportBlur;
    const page = presenceCtx?.page || 'orders';
    if (!show || !modalOrderKey || !focus || !blur) return undefined;
    const key = cellKey(page, 'order', modalOrderKey, 'modal');
    focus(key, { lock: false });
    return () => blur(key);
  }, [
    show,
    modalOrderKey,
    presenceFocus,
    presenceBlur,
    presenceCtx?.reportFocus,
    presenceCtx?.reportBlur,
    presenceCtx?.page,
  ]);

  const orderModalLineIds = useMemo(
    () => (Array.isArray(orderData) ? orderData.map((line) => line?.id).filter(Boolean) : []),
    [orderData]
  );

  const orderModalPresence = useMemo(
    () => readOrderModalPresence(
      presenceCtx?.roster,
      presenceCtx?.page,
      modalOrderKey,
      presenceMe,
      orderModalLineIds
    ),
    [presenceCtx?.roster, presenceCtx?.page, modalOrderKey, presenceMe, orderModalLineIds]
  );
  const orderModalHere = orderModalPresence.users;
  const orderModalCells = orderModalPresence.cells;

  // Part link dropdown: portal + fixed positioning (not clipped by frozen Part column)
  const handleDropdownToggle = useCallback((lineId, buttonElement, partId) => {
    if (openDropdownData.id === lineId) {
      closePartLinkDropdown();
    } else if (buttonElement) {
      const rect = buttonElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const menuWidth = 220;
      const menuHeight = 120;
      const zIndex = 1000005;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openAbove = spaceBelow < menuHeight && spaceAbove > spaceBelow;

      let calculatedTop;
      if (openAbove) {
        calculatedTop = Math.max(5, rect.top - menuHeight);
      } else {
        calculatedTop = rect.bottom + 2;
      }

      // Default: open to the right of the trigger, outside the frozen Part column
      let calculatedLeft = rect.right + 4;
      if (calculatedLeft + menuWidth > viewportWidth - 10) {
        const leftOption = rect.left - menuWidth - 4;
        calculatedLeft = leftOption >= 10 ? leftOption : Math.max(10, viewportWidth - menuWidth - 10);
      }

      setOpenDropdownData({
        id: lineId,
        partId,
        style: {
          position: 'fixed',
          top: `${calculatedTop}px`,
          left: `${calculatedLeft}px`,
          zIndex,
          minWidth: '220px',
          boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
        }
      });
    }
  }, [openDropdownData.id, closePartLinkDropdown]);

  // Memoized handlers for better performance
  const handleViewChange = useCallback((view) => {
    setActiveView(view);
    // ✅ CRITICAL FIX: Close any open dropdown when switching tabs
    closePartLinkDropdown();
  }, [closePartLinkDropdown]);

  const handleSwitchView = useCallback(() => {
    setActiveView(activeView === 'orderLines' ? 'bomParts' : 'orderLines');
    // ✅ CRITICAL FIX: Close any open dropdown when switching tabs
    closePartLinkDropdown();
  }, [activeView, closePartLinkDropdown]);

  const handleDualView = useCallback(() => {
    setActiveView('both');
    // ✅ CRITICAL FIX: Close any open dropdown when switching tabs
    closePartLinkDropdown();
  }, [closePartLinkDropdown]);

  // Comment management functions
  // Aggregate all comments from all lines in this order (same as InlineOrderDropdown)
  const aggregatedComments = useMemo(() => {
    if (!orderData || !Array.isArray(orderData)) return [];
    const allComments = [];
    orderData.forEach((item) => {
      const backendComments = item.comment || [];
      const localComments = commentsMap[item.id] || [];
      const merged = [...backendComments, ...localComments];
      allComments.push(...merged);
    });
    // Sort by date/time (most recent first)
    return [...allComments].sort((a, b) => {
      const dateA = a.dateStr && a.timeStr ? new Date(`${a.dateStr} ${a.timeStr}`) : new Date(0);
      const dateB = b.dateStr && b.timeStr ? new Date(`${b.dateStr} ${b.timeStr}`) : new Date(0);
      return dateB - dateA;
    });
  }, [orderData, commentsMap]);
  
  // Get the first line ID for adding new comments (use first line as default)
  const firstLineId = useMemo(() => {
    if (!orderData || !Array.isArray(orderData) || orderData.length === 0) return null;
    return orderData[0].id;
  }, [orderData]);

  const orderAccessLine = sourceLine || (Array.isArray(orderData) ? orderData[0] : null) || null;

  const headerOrderUrls = useMemo(() => {
    if (!Array.isArray(orderData)) return [];
    const seen = new Set();
    const urls = [];
    orderData.forEach((line) => {
      (line?.order?.orderUrls || []).forEach((item) => {
        if (!item || item.id == null || seen.has(item.id)) return;
        seen.add(item.id);
        urls.push(item);
      });
    });
    return urls;
  }, [orderData]);
  
  const notificationsCtx = useNotifications();
  const commentDock = useCommentDock();
  const commentThreadKey = getCommentThreadKey({ orderLineId: firstLineId });
  const commentChatOpen = Boolean(
    commentDock?.threads?.some((thread) => thread.key === commentThreadKey && !thread.minimized)
  );
  const commentWatchers = useMemo(() => {
    if (!firstLineId || !presenceCtx?.page || !presenceCtx?.roster) return [];
    const key = cellKey(presenceCtx.page, 'line', firstLineId, 'comment');
    return (presenceCtx.roster.cells?.[key] || []).filter((user) => user?.email !== presenceMe);
  }, [firstLineId, presenceCtx?.page, presenceCtx?.roster, presenceMe]);
  const commentUnread = notificationsCtx && firstLineId
    ? notificationsCtx.unreadCountForOrder(orderData?.[0]?.order?.ordernum, firstLineId)
    : 0;

  // Handler for adding comment from modal (uses parent handler)
  const handleModalAddComment = useCallback((newComment, status = "pending") => {
    if (firstLineId && onAddComment) {
      onAddComment(firstLineId, newComment, status);
    }
  }, [firstLineId, onAddComment]);
  
  // Handler for deleting comment from modal (uses parent handler)
  const handleModalDeleteComment = useCallback((commentId) => {
    if (firstLineId && onDeleteComment) {
      onDeleteComment(firstLineId, commentId);
    }
  }, [firstLineId, onDeleteComment]);
  
  // Handler for resolving comment from modal (uses parent handler)
  const handleModalResolveThread = useCallback((commentId = null) => {
    if (firstLineId && onResolveThread) {
      onResolveThread(firstLineId, commentId);
    }
  }, [firstLineId, onResolveThread]);

  // Add these state declarations
  const [submittedLineStatuses, setSubmittedLineStatuses] = useState({});
  const [lineTagsToDeselect, setLineTagsToDeselect] = useState({});

  const orderHasOrderStatusHold = useCallback(() => {
    const hold = ['cc hold', 'wire hold', 'credit hold', 'ppa'];
    if (!orderData?.length) return false;
    return orderData.some((line) => {
      const tags = [
        ...(submittedOrderStatuses[line.id] || []),
        ...(orderStatusSelections[line.id] || []),
      ];
      return tags.some((tag) => hold.includes(String(tag?.label || tag || '').toLowerCase()));
    });
  }, [orderData, submittedOrderStatuses, orderStatusSelections]);

  const orderHasOrderStatusPaid = useCallback(() => {
    if (!orderData?.length) return false;
    return orderData.some((line) => {
      const tags = [
        ...(submittedOrderStatuses[line.id] || []),
        ...(orderStatusSelections[line.id] || []),
      ];
      return tags.some((tag) => String(tag?.label || tag || '').trim().toLowerCase() === 'paid');
    });
  }, [orderData, submittedOrderStatuses, orderStatusSelections]);


  /** Green when this row's Line Status column shows Ready to Ship / Shipped (not order-level report). */
  const lineItemHasInShippingOrShipped = useCallback((item) => {
    if (!item?.id) return false;
    const key = lineStatusKey(item.id);
    const deselected = lineTagsToDeselect[key] || [];
    const activeSubmitted = (submittedLineStatuses[key] || []).filter(
      (tag) => !deselected.some((t) => t.label === tag.label)
    );
    const tags = [...activeSubmitted, ...(lineStatusSelections[key] || [])];
    return tags.some((t) => isInShippingOrShippedLabel(t));
  }, [submittedLineStatuses, lineStatusSelections, lineTagsToDeselect]);

  const lineItemHasStaggingCart = useCallback((item) => {
    if (!item?.id) return false;
    const key = lineStatusKey(item.id);
    const deselected = lineTagsToDeselect[key] || [];
    const activeSubmitted = (submittedLineStatuses[key] || []).filter(
      (tag) => !deselected.some((t) => t.label === tag.label)
    );
    const tags = [...activeSubmitted, ...(lineStatusSelections[key] || [])];
    return tags.some((t) => isStaggingCartLabel(t));
  }, [submittedLineStatuses, lineStatusSelections, lineTagsToDeselect]);

  const lineItemHasOnHoldCart = useCallback((item) => {
    if (!item?.id) return false;
    const key = lineStatusKey(item.id);
    const deselected = lineTagsToDeselect[key] || [];
    const activeSubmitted = (submittedLineStatuses[key] || []).filter(
      (tag) => !deselected.some((t) => t.label === tag.label)
    );
    const tags = [...activeSubmitted, ...(lineStatusSelections[key] || [])];
    return tags.some((t) => isOnHoldCartLabel(t));
  }, [submittedLineStatuses, lineStatusSelections, lineTagsToDeselect]);

  const shouldRowBeGreen = useCallback((item) => {
    if (!item?.id) return false;
    // Staging / On Hold Cart: green even when order has CC/Wire/Credit/PPA hold
    if (lineItemHasStaggingCart(item) || lineItemHasOnHoldCart(item)) return true;
    if (orderHasOrderStatusHold() && !orderHasOrderStatusPaid()) return false;
    return lineItemHasInShippingOrShipped(item);
  }, [lineItemHasInShippingOrShipped, lineItemHasStaggingCart, lineItemHasOnHoldCart, orderHasOrderStatusHold, orderHasOrderStatusPaid]);

  // ✅ ADD THIS: Track pending removal to keep submit button visible
  const [pendingRemoval, setPendingRemoval] = useState({});

  // Update handleLineStatusToggle to track pending removal and apply to all lines when bulk mode is enabled
  const handleLineStatusToggle = (lineId, tag) => {
    const lineIdKey = String(lineId);
    const targetIds = bulkModeEnabled && Array.isArray(orderData)
      ? orderData.map((item) => String(item.id))
      : [lineIdKey];

    const isEffectivelySelected =
      (lineStatusSelections[lineIdKey] || []).some((s) => s.label === tag.label) ||
      (
        (submittedLineStatuses[lineIdKey] || []).some((s) => s.label === tag.label) &&
        !(lineTagsToDeselect[lineIdKey] || []).some((t) => t.label === tag.label)
      );
    const isAdding = !isEffectivelySelected;

    setLineStatusSelections((prev) => {
      const next = { ...prev };
      targetIds.forEach((itemId) => {
        const current = next[itemId] || [];
        const inSel = current.some((s) => s.label === tag.label);
        const inSub = (submittedLineStatuses[itemId] || []).some((s) => s.label === tag.label);

        if (isAdding) {
          if (!inSel && !inSub) {
            next[itemId] = [...current, tag];
          }
        } else if (inSel) {
          next[itemId] = current.filter((s) => s.label !== tag.label);
        }
      });
      return next;
    });

    setLineTagsToDeselect((prev) => {
      const next = { ...prev };
      targetIds.forEach((itemId) => {
        const current = next[itemId] || [];
        const marked = current.some((t) => t.label === tag.label);
        const inSub = (submittedLineStatuses[itemId] || []).some((s) => s.label === tag.label);

        if (isAdding) {
          if (marked) {
            next[itemId] = current.filter((t) => t.label !== tag.label);
          }
        } else if (inSub && !marked) {
          next[itemId] = [...current, tag];
        }
      });
      return next;
    });

    setPendingRemoval((prev) => {
      const next = { ...prev };
      targetIds.forEach((itemId) => {
        if (isAdding) {
          next[itemId] = false;
          return;
        }
        const remainingSelections = (lineStatusSelections[itemId] || []).filter(
          (s) => s.label !== tag.label
        ).length;
        const remainingSubmitted = (submittedLineStatuses[itemId] || []).filter((s) => {
          if (s.label === tag.label) return false;
          return !(lineTagsToDeselect[itemId] || []).some((t) => t.label === s.label);
        }).length;
        next[itemId] = remainingSelections === 0 && remainingSubmitted === 0;
      });
      return next;
    });

    if (bulkModeEnabled) {
      setHasBulkChanges(true);
    }
  };

  // Update handleLineTagDeselect to track pending removal
  const handleLineTagDeselect = (lineId, tag) => {
    const lineIdKey = String(lineId);
    setLineTagsToDeselect(prev => {
      const currentDeselections = prev[lineIdKey] || [];
      const isAlreadyMarked = currentDeselections.some(t => t.label === tag.label);
      const submittedTags = submittedLineStatuses[lineIdKey] || [];

      if (isAlreadyMarked) {
        // Remove from deselection list
        const newDeselections = currentDeselections.filter(t => t.label !== tag.label);

        // ✅ Check if we're no longer removing all tags
        if (newDeselections.length === 0) {
          setPendingRemoval(prevPending => ({
            ...prevPending,
            [lineIdKey]: false
          }));
        }

        return {
          ...prev,
          [lineIdKey]: newDeselections
        };
      } else {
        // Add to deselection list
        const newDeselections = [...currentDeselections, tag];

        // ✅ If we're deselecting all submitted tags, mark as pending removal
        if (newDeselections.length === submittedTags.length && (lineStatusSelections[lineIdKey] || []).length === 0) {
          setPendingRemoval(prevPending => ({
            ...prevPending,
            [lineIdKey]: true
          }));
        }

        if (bulkModeEnabled) {
          setHasBulkChanges(true);
        }

        return {
          ...prev,
          [lineIdKey]: newDeselections
        };
      }
    });
  };

  // Update hasLineChangesToSubmit to include pending removal
  const modalLineStatusLockOwner = useCallback(
    (lineId) => {
      const fromParent = lineStatusLockOwner?.(lineId);
      if (fromParent) return fromParent;
      return readLineStatusPresence(presenceCtx?.roster, presenceCtx?.page, lineId, presenceMe).owner;
    },
    [lineStatusLockOwner, presenceCtx?.roster, presenceCtx?.page, presenceMe]
  );

  const hasLineChangesToSubmit = useCallback((lineId) => {
    const currentSelections = lineStatusSelections[lineId] || [];
    const tagsToRemove = lineTagsToDeselect[lineId] || [];

    // ✅ Include pending removal state
    return currentSelections.length > 0 || tagsToRemove.length > 0 || pendingRemoval[lineId];
  }, [lineStatusSelections, lineTagsToDeselect, pendingRemoval]);

  // Update handleLineStatusSubmit to clear pending removal
  const handleLineStatusSubmit = async (lineId) => {
    const lockedBy = modalLineStatusLockOwner(lineId);
    if (lockedBy) {
      alert(`${lockedBy.name} is editing this line status. Try again in a moment.`);
      return;
    }

    const currentSelections = lineStatusSelections[lineId] || [];
    const submittedSelections = submittedLineStatuses[lineId] || [];
    const tagsToRemove = lineTagsToDeselect[lineId] || [];

    // Calculate final selections
    const finalSelections = [
      ...currentSelections,
      ...submittedSelections.filter(
        tag => !tagsToRemove.some(removeTag => removeTag.label === tag.label)
      )
    ].filter((tag, index, array) =>
      array.findIndex(t => t.label === tag.label) === index
    );

    const submitData = {
      orderLineId: lineId,
      status: JSON.stringify(finalSelections),
    };

    console.log("Submitting lineItemStatus:", submitData);

    try {
      await saveLineStatus(submitData);
      notifyOrderHistoryChanged(orderData?.[0]?.order?.ordernum || orderData?.[0]?.ordernum);

      // Update local state
      setSubmittedLineStatuses(prev => ({
        ...prev,
        [lineId]: finalSelections
      }));

      // Clear pending changes
      setLineStatusSelections(prev => ({
        ...prev,
        [lineId]: []
      }));

      setLineTagsToDeselect(prev => ({
        ...prev,
        [lineId]: []
      }));

      // ✅ CLEAR pending removal state after successful submission
      setPendingRemoval(prev => ({
        ...prev,
        [lineId]: false
      }));

    } catch (error) {
      console.error("Error submitting line status:", error);
      alert("Error submitting line status. Please try again.");
    }
  };

  // Handler for bulk line status selection - stores tag for confirmation, doesn't apply yet
  const handleBulkLineStatusToggle = useCallback((tag) => {
    // Store the selected tag as pending - don't apply it yet
    setPendingBulkTag(tag);
    // Close bulk dropdown
    setBulkLineStatusDropdownOpen(false);
  }, []);

  // Handler for bulk confirmation - applies and saves tag to all lines
  const handleBulkLineStatusConfirm = useCallback(async () => {
    if (!pendingBulkTag || !orderData || !Array.isArray(orderData)) return;
    
    const tag = pendingBulkTag;
    const linesToUpdate = [];
    
    // Apply the tag to all order lines
    orderData.forEach((item) => {
      const itemId = item.id;
      // Check if tag is already selected for this line
      const currentSelections = lineStatusSelections[itemId] || [];
      const submittedSelections = submittedLineStatuses[itemId] || [];
      const isAlreadySelected = currentSelections.some(selection => selection.label === tag.label) ||
                               submittedSelections.some(selection => selection.label === tag.label);
      
      // Only add if not already selected
      if (!isAlreadySelected) {
        handleLineStatusToggle(itemId, tag);
        linesToUpdate.push(itemId);
      }
    });
    
    // Clear pending tag
    setPendingBulkTag(null);
    
    // Auto-submit/save for all lines that were updated
    // Use setTimeout to ensure state updates are processed first
    setTimeout(() => {
      linesToUpdate.forEach((itemId) => {
        if (hasLineChangesToSubmit && hasLineChangesToSubmit(itemId)) {
          handleLineStatusSubmit(itemId);
        }
      });
    }, 100);
  }, [pendingBulkTag, orderData, lineStatusSelections, submittedLineStatuses, handleLineStatusToggle, hasLineChangesToSubmit, handleLineStatusSubmit]);

  // New handler for bulk submit button - submits all changes with loading overlay
  const handleBulkSubmit = useCallback(async () => {
    if (!orderData || !Array.isArray(orderData) || !hasBulkChanges) return;
    
    setIsSubmittingBulk(true);
    
    try {
      // Collect all lines that have changes
      const linesToSubmit = [];
      orderData.forEach((item) => {
        const itemId = String(item.id);
        if (hasLineChangesToSubmit(itemId)) {
          linesToSubmit.push(itemId);
        }
      });
      
      // Submit each line one by one (API accepts one at a time)
      for (const lineId of linesToSubmit) {
        const currentSelections = lineStatusSelections[lineId] || [];
        const submittedSelections = submittedLineStatuses[lineId] || [];
        const tagsToRemove = lineTagsToDeselect[lineId] || [];

        // Calculate final selections
        const finalSelections = [
          ...currentSelections,
          ...submittedSelections.filter(
            tag => !tagsToRemove.some(removeTag => removeTag.label === tag.label)
          )
        ].filter((tag, index, array) =>
          array.findIndex(t => t.label === tag.label) === index
        );

        const submitData = {
          orderLineId: lineId,
          status: JSON.stringify(finalSelections),
        };

        console.log("Submitting bulk lineItemStatus:", submitData);
        await saveLineStatus(submitData);
        notifyOrderHistoryChanged(orderData?.[0]?.order?.ordernum || orderData?.[0]?.ordernum);

        // Update local state for this line
        setSubmittedLineStatuses(prev => ({
          ...prev,
          [lineId]: finalSelections
        }));

        setLineStatusSelections(prev => ({
          ...prev,
          [lineId]: []
        }));

        setLineTagsToDeselect(prev => ({
          ...prev,
          [lineId]: []
        }));

        setPendingRemoval(prev => ({
          ...prev,
          [lineId]: false
        }));
      }
      
      // Clear bulk changes flag
      setHasBulkChanges(false);
      alert(`Successfully updated ${linesToSubmit.length} order lines!`);
    } catch (error) {
      console.error("Error submitting bulk line status:", error);
      alert("Error submitting bulk line status. Please try again.");
    } finally {
      setIsSubmittingBulk(false);
    }
  }, [orderData, hasBulkChanges, hasLineChangesToSubmit, lineStatusSelections, submittedLineStatuses, lineTagsToDeselect, pendingRemoval]);

  // Toggle bulk dropdown
  const toggleBulkLineStatusDropdown = useCallback((event) => {
    event.stopPropagation();
    if (bulkLineStatusTriggerRef.current) {
      const rect = bulkLineStatusTriggerRef.current.getBoundingClientRect();
      const menuMax = 200;
      const gutter = 8;
      const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - gutter);
      const spaceAbove = Math.max(0, rect.top - gutter);
      const openAbove = spaceBelow < menuMax && spaceAbove > spaceBelow;
      const maxHeight = Math.min(menuMax, Math.max(80, openAbove ? spaceAbove : spaceBelow));
      let top = openAbove ? rect.top - maxHeight - 2 : rect.bottom + 2;
      const bottomLimit = window.innerHeight - gutter;
      if (top + maxHeight > bottomLimit) top = Math.max(gutter, bottomLimit - maxHeight);
      if (top < gutter) top = gutter;

      setBulkLineStatusDropdownPosition({
        top,
        left: rect.left,
        width: rect.width,
        maxHeight,
        openAbove
      });
    }
    setBulkLineStatusDropdownOpen(prev => !prev);
  }, []);

  const getPartDisplayNumber = (prcpart) => {
    const partNumber = String(prcpart || '');
    return partNumber.length >= 3 ? partNumber.substring(3) : partNumber || 'N/A';
  };

  const getBomComponentDropdownId = (prcpart) => `bom-comp-${prcpart}`;

  const renderPartLinkTrigger = (displayText, dropdownId, partId, title = 'View Part Details') => {
    if (!partId) {
      return <span>{displayText}</span>;
    }

    return (
      <div className="d-flex justify-content-between align-items-center" style={{ minHeight: '28px' }}>
        <span>{displayText}</span>
        <div className="dropdown d-inline-block ms-2">
          <button
            className={`btn btn-outline-primary btn-sm part-link-trigger dropdown-button-line-${dropdownId}`}
            type="button"
            onClick={(e) => handleDropdownToggle(dropdownId, e.currentTarget, partId)}
            aria-expanded={openDropdownData.id === dropdownId}
            title={title}
            style={{
              padding: '4px 8px',
              fontSize: '0.75rem',
              minWidth: '28px',
              minHeight: '28px',
              borderRadius: '6px',
              border: '2px solid #0078d4',
              backgroundColor: '#f8f9fa'
            }}
          >
            <i className="fa fa-link" aria-hidden="true" style={{ fontSize: '0.8rem', color: '#0078d4' }}></i>
          </button>
        </div>
      </div>
    );
  };

  // Render bulk line status dropdown (portal — not clipped by table scroll)
  const renderPartLinkDropdown = () => {
    if (!openDropdownData.id || !openDropdownData.partId) {
      return null;
    }

    const { id: lineId, partId, style } = openDropdownData;

    return createPortal(
      <ul
        className={`dropdown-menu dropdown-menu-end show dropdown-menu-line-${lineId} part-link-dropdown-menu`}
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        <li>
          <a className="dropdown-item" href={`https://gigaflightinc.cetecerp.com/part/${partId}/inventory`} target="_blank" rel="noreferrer">
            <i className="fa fa-boxes me-2" aria-hidden="true" style={{ color: '#28a745' }}></i>Inventory
          </a>
        </li>
        <li>
          <a className="dropdown-item" href={`https://gigaflightinc.cetecerp.com/part/${partId}/waterfall`} target="_blank" rel="noreferrer">
            <i className="fa fa-chart-line me-2" aria-hidden="true" style={{ color: '#ffc107' }}></i>Waterfall
          </a>
        </li>
        <li>
          <a className="dropdown-item" href={`https://gigaflightinc.cetecerp.com/part/${partId}/pos`} target="_blank" rel="noreferrer">
            <i className="fa fa-shopping-cart me-2" aria-hidden="true" style={{ color: '#007bff' }}></i>Purchase Orders
          </a>
        </li>
      </ul>,
      document.body
    );
  };

  const renderBulkLineStatusDropdown = () => {
    if (!bulkLineStatusDropdownOpen || !bulkLineStatusDropdownPosition) {
      return null;
    }

    return createPortal(
      <div
        className="bulk-line-status-dropdown-menu"
        style={{
          position: 'fixed',
          top: `${bulkLineStatusDropdownPosition.top}px`,
          left: `${bulkLineStatusDropdownPosition.left}px`,
          width: `${bulkLineStatusDropdownPosition.width}px`,
          zIndex: 1000003,
          backgroundColor: 'white',
          border: '1px solid #ced4da',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          maxHeight: `${bulkLineStatusDropdownPosition.maxHeight || 200}px`,
          overflowY: 'auto',
          marginTop: 0
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {LINE_STATUS_TAGS.map((tag, tagIndex) => (
          <div
            key={tagIndex}
            className="p-2 d-flex align-items-center"
            style={{
              cursor: 'pointer',
              backgroundColor: 'white',
              borderBottom: '1px solid #f1f1f1',
              fontSize: '12px'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleBulkLineStatusToggle(tag);
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
          >
            <div
              className="color-indicator me-2"
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: tag.color,
                border: '1px solid rgba(0, 0, 0, 0.3)',
                display: 'inline-block',
                flexShrink: 0,
                minWidth: '10px',
                minHeight: '10px'
              }}
              title={`Color: ${tag.color}`}
            ></div>
            <span style={{ fontSize: '0.75rem' }}>
              {tag.label}
            </span>
          </div>
        ))}
      </div>,
      document.body
    );
  };

  const renderLineStatusDropdownMenus = () => {
    return Object.keys(dropdownOpen)
      .filter((lineId) => dropdownOpen[lineId] && lineStatusDropdownPositions[lineId])
      .map((lineId) => {
        const position = lineStatusDropdownPositions[lineId];
        return createPortal(
          <div
            key={`line-status-menu-${lineId}`}
            className="line-status-dropdown-menu"
            style={{
              position: 'fixed',
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width}px`,
              zIndex: 1000004,
              backgroundColor: 'white',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              maxHeight: `${position.maxHeight || 200}px`,
              overflowY: 'auto',
              marginTop: 0
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {LINE_STATUS_TAGS.map((tag, tagIndex) => {
              const isMarkedForDeselection = (lineTagsToDeselect[lineId] || []).some(
                (t) => lineStatusLabelsEqual(t.label, tag.label)
              );
              const isSelected = (
                (lineStatusSelections[lineId] || []).some(
                  (selection) => lineStatusLabelsEqual(selection.label, tag.label)
                ) || (
                  (submittedLineStatuses[lineId] || []).some(
                    (selection) => lineStatusLabelsEqual(selection.label, tag.label)
                  ) && !isMarkedForDeselection
                )
              );
              return (
                <div
                  key={tagIndex}
                  className="p-2 d-flex align-items-center"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#f8f9fa' : 'white',
                    borderBottom: '1px solid #f1f1f1'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLineStatusToggle(lineId, tag);
                  }}
                >
                  <div
                    className="color-indicator me-2"
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '2px',
                      backgroundColor: tag.color,
                      border: '2px solid rgba(0, 0, 0, 0.3)',
                      display: 'inline-block',
                      flexShrink: 0,
                      minWidth: '12px',
                      minHeight: '12px'
                    }}
                    title={`Color: ${tag.color}`}
                  ></div>
                  <span style={{ fontSize: '0.875rem' }}>
                    {tag.label}
                  </span>
                  {isSelected && (
                    <span className="ms-auto">✓</span>
                  )}
                </div>
              );
            })}
          </div>,
          document.body
        );
      });
  };


  // Logic to close the dropdown on click outside
  // useEffect(() => {
  //   function handleClickOutside(event) {
  //     // 1. If no dropdown is open, do nothing.
  //     if (!openDropdownData.id) return;

  //     // 2. The click must NOT be inside the currently open dropdown (which is styled via the state).
  //     // Since we cannot easily use a ref on the dynamically created dropdown, 
  //     // we check if the click is outside the main Modal content area that contains the buttons.
  //     // A better approach is to check if the click target is the button or the menu itself.

  //     // Get the dropdown button element if a dropdown is open.
  //     // We assume the button has the class 'dropdown-button-line-<id>' for easy selection.
  //     const button = document.querySelector(`.dropdown-button-line-${openDropdownData.id}`);
  //     const dropdownMenu = document.querySelector(`.dropdown-menu-line-${openDropdownData.id}`);

  //     const isButtonClick = button && button.contains(event.target);
  //     const isMenuClick = dropdownMenu && dropdownMenu.contains(event.target);

  //     if (!isButtonClick && !isMenuClick) {
  //       // Clicked outside both the button and the menu -> Close dropdown
  //       setOpenDropdownData({ id: null, style: {} });
  //     }
  //   }

  //   // Attach the event listener when the component mounts or dependencies change
  //   document.addEventListener("mousedown", handleClickOutside);

  //   // Clean up the event listener when the component unmounts or dependencies change
  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //   };
  // }, [openDropdownData.id]); // Re-run effect only when dropdown state changes

  useEffect(() => {
    if (!openDropdownData.id) return;

    function handleClickOutside(event) {
      const dropdownMenu = document.querySelector('.part-link-dropdown-menu');
      const isMenuClick = dropdownMenu && dropdownMenu.contains(event.target);
      const isButtonClick = event.target.closest?.('.part-link-trigger');

      if (!isButtonClick && !isMenuClick) {
        closePartLinkDropdown();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownData.id, closePartLinkDropdown]);

  // ✅ Group BOM parts by bom.prcpart - Memoized for performance
  const { aggregatedBomParts, bomPartsCount, parentPartIdMap } = useMemo(() => {
    const bomPartMap = new Map();
    const parentPartIdMapping = new Map(); // Map part number to part ID

    if (Array.isArray(orderData)) {
      orderData.forEach((item) => {
        const bomParts = item?.bom?.bomParts || [];
        const lineShipQty = Number(item.balancedue) || 0;
        const lineItemStatus = item.lineItemStatus;
        const parentPart = item.prcpart || '';
        const parentPartId = item?.part?.id;

        // Store mapping of parent part number to part ID
        if (parentPart && parentPartId) {
          parentPartIdMapping.set(parentPart, parentPartId);
        }

        bomParts.forEach((bom) => {
          const key = bom.prcpart;
          if (!key) return;

          const isArchived = !!bom.archived;
          const qtyOrder = isArchived ? 0 : (bom.qty_per_top || 0) * lineShipQty;
          const part = bom.part || {};
          const qohBom = Number(part.qoh_cache) || 0;
          const qooBom = Number(part.qoo_cache) || 0;
          const qrBom = Number(part.qr_cache) || 0;
          const occurrence = { parentPart: item.prcpart, bomPartId: bom.id, archived: isArchived };

          if (bomPartMap.has(key)) {
            const existing = bomPartMap.get(key);
            existing.qty_order += qtyOrder;
            existing.parentParts.add(item.prcpart);
            existing.occurrences.push(occurrence);
          } else {
            bomPartMap.set(key, {
              prcpart: key,
              qty_order: qtyOrder,
              qty_per_top: bom.qty_per_top || 0,
              qohBom,
              qooBom,
              qrBom,
              qtyAvail: qohBom - qrBom,
              parentParts: new Set([item.prcpart]),
              occurrences: [occurrence],
              part,
            });
          }
        });
      });
    }

    const aggregatedBomParts = Array.from(bomPartMap.values());
    return { aggregatedBomParts, bomPartsCount: aggregatedBomParts.length, parentPartIdMap: parentPartIdMapping };
  }, [orderData]);

  // Archive/Unarchive button for a single, unambiguous BOM part row (per-line BOM details tables).
  const renderBomArchiveCell = (bom) => {
    const isArchived = !!bom?.archived;

    return (
      <button
        type="button"
        className={`btn btn-sm ${isArchived ? 'btn-outline-success' : 'btn-outline-warning'}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!onArchiveBomPart || !bom?.id) return;
          onArchiveBomPart(bom.id, !isArchived, modalOrderKey);
        }}
        disabled={!onArchiveBomPart || !bom?.id}
        title={isArchived ? 'Unarchive this BOM part' : 'Archive this BOM part'}
        style={{ fontSize: '0.7rem', padding: '2px 8px' }}
      >
        {isArchived ? 'Unarchive' : 'Archive'}
      </button>
    );
  };

  // Archive/Unarchive button for an aggregated (merged) BOM Parts row.
  // Targets the first underlying BOMParts id for that component.
  const renderAggregatedArchiveCell = (line) => {
    const target = (line.occurrences || [])[0];
    if (!target) {
      return <span className="text-muted">-</span>;
    }
    const isArchived = !!target.archived;

    return (
      <button
        type="button"
        className={`btn btn-sm ${isArchived ? 'btn-outline-success' : 'btn-outline-warning'}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!onArchiveBomPart || !target.bomPartId) return;
          onArchiveBomPart(target.bomPartId, !isArchived, modalOrderKey);
        }}
        disabled={!onArchiveBomPart || !target.bomPartId}
        title={isArchived ? 'Unarchive this BOM part' : 'Archive this BOM part'}
        style={{ fontSize: '0.7rem', padding: '2px 8px' }}
      >
        {isArchived ? 'Unarchive' : 'Archive'}
      </button>
    );
  };

  // Order Lines column sort (ascending/descending) - modal
  const [orderLinesSort, setOrderLinesSort] = useState({ column: 'shipDate', direction: 'ascending' });
  const handleOrderLinesSort = (column) => {
    setOrderLinesSort(prev => ({
      column,
      direction: prev.column === column ? (prev.direction === 'ascending' ? 'descending' : 'ascending') : 'ascending'
    }));
  };
  const getOrderLinesSortIcon = (column) => {
    if (orderLinesSort.column !== column) return '';
    return orderLinesSort.direction === 'ascending' ? ' ▲' : ' ▼';
  };
  const sortedOrderData = useMemo(() => {
    if (!Array.isArray(orderData) || orderData.length === 0) return orderData || [];
    const { column, direction } = orderLinesSort;
    const asc = direction === 'ascending';
    return [...orderData].sort((a, b) => {
      let valA, valB;
      switch (column) {
        case 'line':
          valA = Number(a.lineitem) || 0;
          valB = Number(b.lineitem) || 0;
          return asc ? valA - valB : valB - valA;
        case 'part':
          valA = String(a.prcpart || '').toLowerCase();
          valB = String(b.prcpart || '').toLowerCase();
          return asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'shipDate': {
          const dateA = a.promisedate || '';
          const dateB = b.promisedate || '';
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return asc ? new Date(dateA) - new Date(dateB) : new Date(dateB) - new Date(dateA);
        }
        case 'shipQty':
          valA = Number(a.balancedue) || 0;
          valB = Number(b.balancedue) || 0;
          return asc ? valA - valB : valB - valA;
        case 'qoh':
          valA = Number(a.part?.qoh_cache) || 0;
          valB = Number(b.part?.qoh_cache) || 0;
          return asc ? valA - valB : valB - valA;
        case 'qoo':
          valA = Number(a.part?.qoo_cache) || 0;
          valB = Number(b.part?.qoo_cache) || 0;
          return asc ? valA - valB : valB - valA;
        case 'qr':
          valA = Number(a.part?.qr_cache) || 0;
          valB = Number(b.part?.qr_cache) || 0;
          return asc ? valA - valB : valB - valA;
        case 'qtyAvail': {
          const qohA = Number(a.part?.qoh_cache) || 0, qrA = Number(a.part?.qr_cache) || 0;
          const qohB = Number(b.part?.qoh_cache) || 0, qrB = Number(b.part?.qr_cache) || 0;
          return asc ? (qohA - qrA) - (qohB - qrB) : (qohB - qrB) - (qohA - qrA);
        }
        default:
          return 0;
      }
    });
  }, [orderData, orderLinesSort]);

  // BOM Parts column sort - modal
  const [bomPartsSort, setBomPartsSort] = useState({ column: 'qtyAvail', direction: 'ascending' });
  const handleBomPartsSort = (column) => {
    setBomPartsSort(prev => ({
      column,
      direction: prev.column === column ? (prev.direction === 'ascending' ? 'descending' : 'ascending') : 'ascending'
    }));
  };
  const getBomPartsSortIcon = (column) => {
    if (bomPartsSort.column !== column) return '';
    return bomPartsSort.direction === 'ascending' ? ' ▲' : ' ▼';
  };
  const sortedAggregatedBomParts = useMemo(() => {
    if (!aggregatedBomParts || aggregatedBomParts.length === 0) return aggregatedBomParts || [];
    const { column, direction } = bomPartsSort;
    const asc = direction === 'ascending';
    return [...aggregatedBomParts].sort((a, b) => {
      let valA, valB;
      switch (column) {
        case 'topLevel': {
          const arrA = Array.from(a.parentParts || []);
          const arrB = Array.from(b.parentParts || []);
          valA = (arrA[0] || '').toString().toLowerCase();
          valB = (arrB[0] || '').toString().toLowerCase();
          return asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
        }
        case 'componentPart':
          valA = String(a.prcpart || '').toLowerCase();
          valB = String(b.prcpart || '').toLowerCase();
          return asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'qtyOrder':
          valA = Number(a.qty_order) || 0;
          valB = Number(b.qty_order) || 0;
          return asc ? valA - valB : valB - valA;
        case 'qoh':
          valA = Number(a.qohBom) || 0;
          valB = Number(b.qohBom) || 0;
          return asc ? valA - valB : valB - valA;
        case 'qoo':
          valA = Number(a.qooBom) || 0;
          valB = Number(b.qooBom) || 0;
          return asc ? valA - valB : valB - valA;
        case 'qr':
          valA = Number(a.qrBom) || 0;
          valB = Number(b.qrBom) || 0;
          return asc ? valA - valB : valB - valA;
        case 'qtyAvail':
          valA = Number(a.qtyAvail) || 0;
          valB = Number(b.qtyAvail) || 0;
          return asc ? valA - valB : valB - valA;
        default:
          return 0;
      }
    });
  }, [aggregatedBomParts, bomPartsSort]);

  // Export Order Lines to Excel (current sorted view)
  const handleExportOrderLines = useCallback(() => {
    if (!sortedOrderData || sortedOrderData.length === 0) {
      alert('No order lines to export.');
      return;
    }
    const orderNum = orderData[0]?.order?.ordernum || 'order';
    const headers = [['Line', 'Part', 'Ship Date', 'QOH', 'QOO', 'QR', 'Qty Avail']];
    const rows = sortedOrderData.map((item) => {
      const qoh = Number(item.part?.qoh_cache) || 0;
      const qr = Number(item.part?.qr_cache) || 0;
      const qtyAvail = qoh - qr;
      const partDisplay = String(item.prcpart || '').length >= 3 ? String(item.prcpart || '').substring(3) : (item.prcpart || 'N/A');
      return [
        item.lineitem,
        partDisplay,
        item.promisedate || 'N/A',
        qoh,
        Number(item.part?.qoo_cache) || 0,
        qr,
        qtyAvail
      ];
    });
    const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Order Lines');
    XLSX.writeFile(workbook, `order_${orderNum}_lines.xlsx`);
  }, [sortedOrderData, orderData]);

  // Export BOM Parts to Excel (current sorted view)
  const handleExportBomParts = useCallback(() => {
    if (!sortedAggregatedBomParts || sortedAggregatedBomParts.length === 0) {
      alert('No BOM parts to export.');
      return;
    }
    const orderNum = orderData[0]?.order?.ordernum || 'order';
    const headers = [['S.No', 'Top Level Parts', 'Component Part', 'Qty/Order', 'QOH', 'QOO', 'QR', 'Qty Avail']];
    const rows = sortedAggregatedBomParts.map((entry, idx) => {
      const topLevel = Array.from(entry.parentParts || []).join('; ') || 'N/A';
      const partDisplay = String(entry.prcpart || '').length >= 3 ? String(entry.prcpart || '').substring(3) : (entry.prcpart || 'N/A');
      return [
        idx + 1,
        topLevel,
        partDisplay,
        entry.qty_order ?? 'N/A',
        entry.qohBom ?? 'N/A',
        entry.qooBom ?? 'N/A',
        entry.qrBom ?? 'N/A',
        entry.qtyAvail ?? 'N/A'
      ];
    });
    const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'BOM Parts');
    XLSX.writeFile(workbook, `order_${orderNum}_bom_parts.xlsx`);
  }, [sortedAggregatedBomParts, orderData]);

  // Optimized hover handlers
  const handleRowHover = useCallback((e, isHovering, panelType = 'orderLines', isGreenRow = false) => {
    if (isGreenRow) {
      return;
    }
    if (isHovering) {
      e.currentTarget.style.backgroundColor = panelType === 'orderLines' ? '#f8f9fa' : '#e3f2fd';
    } else {
      e.currentTarget.style.backgroundColor = '';
    }
  }, []);


  useEffect(() => {
    if (!orderData || !Array.isArray(orderData)) {
      return;
    }

    const submitted = {};
    orderData.forEach((line) => {
      if (line?.lineItemStatus) {
        try {
          submitted[lineStatusKey(line.id)] = JSON.parse(line.lineItemStatus);
        } catch (e) {
          console.error("Invalid JSON in lineItemStatus:", e);
        }
      }
    });

    setSubmittedLineStatuses(submitted);
    setLineStatusSelections({});
    setLineTagsToDeselect({});
    setPendingRemoval({});
  }, [orderData]);

  // Initialize Order Status from orderData when modal opens
  useEffect(() => {
    if (orderData && Array.isArray(orderData) && orderData.length > 0) {
      const firstLine = orderData[0];
      const lineId = firstLine.id;
      
      // Load Order Status from manualUpdateStatus
      if (firstLine?.manualUpdateStatus) {
        try {
          const orderStatusTags = JSON.parse(firstLine.manualUpdateStatus);
          setSubmittedOrderStatuses((prev) => ({
            ...prev,
            [lineId]: orderStatusTags,
          }));
        } catch (e) {
          console.error("Invalid JSON in manualUpdateStatus:", e);
        }
      }
    }
  }, [orderData, show]);

  // Handle Order Status tag selection
  const handleOrderStatusToggle = useCallback((lineId, tag) => {
    setOrderStatusSelections(prev => {
      const currentSelections = prev[lineId] || [];
      const isSelected = currentSelections.some(selection => selection.label === tag.label);

      if (isSelected) {
        // Remove tag if already selected
        return {
          ...prev,
          [lineId]: currentSelections.filter(selection => selection.label !== tag.label)
        };
      } else {
        // Add tag if not selected
        return {
          ...prev,
          [lineId]: [...currentSelections, tag]
        };
      }
    });
  }, []);

  // Handle individual tag deselection by clicking on the tag
  const handleOrderStatusTagDeselect = useCallback((lineId, tag) => {
    setTagsToDeselect(prev => {
      const currentDeselections = prev[lineId] || [];
      const isMarkedForDeselection = currentDeselections.some(t => t.label === tag.label);

      if (isMarkedForDeselection) {
        // Remove from deselection list
        return {
          ...prev,
          [lineId]: currentDeselections.filter(t => t.label !== tag.label)
        };
      } else {
        // Add to deselection list
        return {
          ...prev,
          [lineId]: [...currentDeselections, tag]
        };
      }
    });
  }, []);

  // Check if there are changes to submit
  const hasOrderStatusChangesToSubmit = useCallback((lineId) => {
    const currentSelections = orderStatusSelections[lineId] || [];
    const submittedSelections = submittedOrderStatuses[lineId] || [];
    const tagsToRemove = tagsToDeselect[lineId] || [];

    // Check if there are new selections to submit
    const hasNewSelections = currentSelections.length > 0;

    // Check if there are tags marked for deselection
    const hasTagsToDeselect = tagsToRemove.length > 0;

    return hasNewSelections || hasTagsToDeselect;
  }, [orderStatusSelections, submittedOrderStatuses, tagsToDeselect]);

  // Handle Order Status submission/deselection
  const handleOrderStatusSubmit = useCallback(async (lineId) => {
    const currentSelections = orderStatusSelections[lineId] || [];
    const submittedSelections = submittedOrderStatuses[lineId] || [];
    const tagsToRemove = tagsToDeselect[lineId] || [];

    try {
      // Start with existing submitted selections (or empty)
      let newSubmitted = Array.isArray(submittedSelections) ? [...submittedSelections] : [];

      // Apply deselections first
      if (tagsToRemove.length > 0) {
        newSubmitted = newSubmitted.filter(
          tag => !tagsToRemove.some(removeTag => removeTag.label === tag.label)
        );
      }

      // Merge new selections into the submitted list (avoid duplicate labels)
      if (currentSelections.length > 0) {
        currentSelections.forEach(sel => {
          if (!newSubmitted.some(t => t.label === sel.label)) {
            newSubmitted.push(sel);
          }
        });

        // Clear the current selection inputs for this line
        setOrderStatusSelections(prev => ({
          ...prev,
          [lineId]: []
        }));
      }

      // Prepare payload using the merged resulting statuses so backend receives the final state
      const payload = {
        status: JSON.stringify(newSubmitted),
        orderLineId: lineId,
        // Names the user on the order history entry and on the PO Review
        // notification raised for Customer Service and the Warehouse.
        lastEditedBy: localStorage.getItem('userEmail')
          || localStorage.getItem('email')
          || localStorage.getItem('userName')
          || ''
      };

      // Persist to backend
      await saveComment(payload);
      notifyOrderHistoryChanged(orderData?.[0]?.order?.ordernum || orderData?.[0]?.ordernum);

      // Close dropdown after successful save
      setOrderStatusDropdownOpen(false);

      // Update submitted statuses locally
      setSubmittedOrderStatuses(prev => ({
        ...prev,
        [lineId]: newSubmitted
      }));

      // Clear the deselection list for this line
      if (tagsToRemove.length > 0) {
        setTagsToDeselect(prev => ({
          ...prev,
          [lineId]: []
        }));
      }

    } catch (err) {
      console.error('Failed to save order status:', err);
      alert('Error saving order status. Please try again.');
    }
  }, [orderStatusSelections, submittedOrderStatuses, tagsToDeselect]);

  // Handle clicking outside Order Status dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inOrderStatus = event.target.closest?.('.order-popup-info-order-status-col');
      const inOrderStatusMenu = event.target.closest?.('.order-status-dropdown-menu');
      if (inOrderStatus || inOrderStatusMenu) {
        return;
      }
      if (orderStatusDropdownRef.current && !orderStatusDropdownRef.current.contains(event.target)) {
        setOrderStatusDropdownOpen(false);
      }
    };

    if (orderStatusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [orderStatusDropdownOpen]);

  const updateOrderStatusDropdownPosition = useCallback(() => {
    if (!orderStatusBoxRef.current) return;
    const rect = orderStatusBoxRef.current.getBoundingClientRect();
    setOrderStatusDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 200)
    });
  }, []);

  useEffect(() => {
    if (!orderStatusDropdownOpen) return;
    updateOrderStatusDropdownPosition();
    window.addEventListener('resize', updateOrderStatusDropdownPosition);
    window.addEventListener('scroll', updateOrderStatusDropdownPosition, true);
    return () => {
      window.removeEventListener('resize', updateOrderStatusDropdownPosition);
      window.removeEventListener('scroll', updateOrderStatusDropdownPosition, true);
    };
  }, [orderStatusDropdownOpen, updateOrderStatusDropdownPosition]);

  const toggleOrderStatusDropdown = useCallback((e) => {
    e?.stopPropagation?.();
    setOrderStatusDropdownOpen((prev) => {
      if (prev) {
        return false;
      }
      updateOrderStatusDropdownPosition();
      return true;
    });
  }, [updateOrderStatusDropdownPosition]);

  const renderOrderStatusDropdownMenu = () => {
    if (!orderStatusDropdownOpen || !orderStatusDropdownPosition || !firstLineId) {
      return null;
    }

    const { top, left, width } = orderStatusDropdownPosition;

    return createPortal(
      <div
        className="order-status-dropdown-menu"
        style={{
          position: 'fixed',
          top: `${top}px`,
          left: `${left}px`,
          width: `${width}px`,
          zIndex: 1000002,
          backgroundColor: 'white',
          border: '1px solid #ced4da',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxHeight: '220px',
          overflowY: 'auto'
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {ORDER_STATUS_TAGS.map((tag, index) => {
          const isInSelections = (orderStatusSelections[firstLineId] || []).some(
            (selection) => selection.label === tag.label
          );
          const isInSubmitted = (submittedOrderStatuses[firstLineId] || []).some(
            (selection) => selection.label === tag.label
          );
          const isMarkedForDeselection = (tagsToDeselect[firstLineId] || []).some(
            (t) => t.label === tag.label
          );
          const isSelected = (isInSelections || isInSubmitted) && !isMarkedForDeselection;
          return (
            <div
              key={index}
              className="p-2 d-flex align-items-center"
              style={{
                cursor: 'pointer',
                backgroundColor: isSelected ? '#f8f9fa' : 'white',
                borderBottom: '1px solid #f1f1f1'
              }}
              onClick={() => handleOrderStatusToggle(firstLineId, tag)}
            >
              {tag.icon ? (
                <i className={`${tag.icon} me-2`} aria-hidden="true" style={{ color: tag.color, fontSize: '14px' }}></i>
              ) : (
                <div
                  className="me-2"
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    backgroundColor: tag.color
                  }}
                ></div>
              )}
              <span style={{ fontSize: '0.875rem' }}>{tag.label}</span>
              {isSelected && <span className="ms-auto">✓</span>}
            </div>
          );
        })}
      </div>,
      document.body
    );
  };


  // Memoized styles for better performance
  const dualViewStyles = useMemo(() => ({
    orderLinesHeader: {
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      padding: '12px 16px',
      borderRadius: '8px 8px 0 0',
      border: '1px solid #dee2e6',
      borderBottom: 'none'
    },
    bomPartsHeader: {
      background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
      padding: '12px 16px',
      borderRadius: '8px 8px 0 0',
      border: '1px solid #90caf9',
      borderBottom: 'none'
    },
    orderLinesTable: {
      border: '1px solid #dee2e6',
      borderTop: 'none',
      borderRadius: '0 0 8px 8px'
    },
    bomPartsTable: {
      border: '1px solid #90caf9',
      borderTop: 'none',
      borderRadius: '0 0 8px 8px'
    },
    rowStyle: {
      cursor: 'pointer'
    },

    // ✅ UNCOMMENTED/ADDED: This is the critical relative positioning for the dropdown parent
    parentPartsCell: {
      textAlign: 'left',
      position: 'relative', // CRITICAL for position: absolute dropdown
      minHeight: '30px',
      wordBreak: 'break-word'
    },

    // ✅ UNCOMMENTED/ADDED: This is the critical relative positioning for the dropdown parent
    orderLinePartCell: {
      position: 'relative', // CRITICAL for position: absolute dropdown
      minHeight: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },

    // Table cell styles for better text handling
    tableCell: {
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      maxWidth: '0'
    }
  }), []);

  return (
    <Modal show={show} onHide={onClose} className="order-popup-modal-root" dialogClassName="modal-xxl modal-dialog-centered" centered size="xl" enforceFocus={false}>
      <Modal.Header closeButton>
        <Modal.Title as="div" className="order-popup-title">
          <span>Order Details</span>
          <PresenceBar
            here={orderModalHere}
            cells={orderModalCells}
            connected={Boolean(presenceCtx?.connected)}
            me={presenceMe}
            size={24}
            label="On this order"
          />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0" style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        {orderData ? (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Order Info Header - Equal spacing layout */}
            <div className="p-3 border-bottom bg-light order-popup-info-header" style={{ flexShrink: 0, paddingRight: '28px' }}>
              <div className="order-popup-info-grid">
                <div className="order-popup-info-primary-cluster">
                <div className="order-popup-info-segment order-popup-info-customer">
                  <div className="order-popup-info-field-line order-popup-info-field-stacked">
                    <div className="order-popup-info-field-label-row">
                      <b>Customer Name</b>:
                    </div>
                    <div className="order-popup-info-field-value-row">
                      {orderData[0].order.customer?.id ? (
                        <a
                          href={`https://gigaflightinc.cetecerp.com/customer/${orderData[0].order.customer.id}/view`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="order-popup-info-field-value"
                          style={{ textDecoration: 'none' }}
                        >
                          {orderData[0].order.customer.name}
                        </a>
                      ) : (
                        <span className="order-popup-info-field-value">{orderData[0].order.customer.name}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="order-popup-info-segment order-popup-info-ordernum-wo">
                <div className="order-popup-order-num-row">
                  <b>Order Num</b>: <a 
                    href={`https://gigaflightinc.cetecerp.com/order/MN${orderData[0].order.ordernum}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    {modalOrderNumberLabel}
                  </a>
                  <a
                    href={orderFolderLink || '#'}
                    onClick={handleOrderFolderClick}
                    target={orderFolderLink ? '_blank' : undefined}
                    rel={orderFolderLink ? 'noreferrer' : undefined}
                    style={{ 
                      cursor: orderFolderLink ? 'pointer' : 'not-allowed',
                      textDecoration: 'none'
                    }}
                    title={orderFolderTitle}
                  >
                    <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: orderFolderLink ? '#ff8c00' : '#cccccc' }}></i>
                  </a>
                </div>
                
                <div className="order-popup-wo-row">
                  <b>WO#</b>: <a 
                    href={`https://gigaflightinc.cetecerp.com/order/MN${orderData[0].order?.woNum}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    {orderData[0].order?.woNum || 'N/A'}
                  </a>
                  {orderData[0].order?.woNum && orderData[0].order.woNum !== '-' && orderData[0].order.woNum !== 'N/A' && (
                    <a
                      href={orderData[0].order?.woUrl || '#'}
                      onClick={(event) => {
                        if (!orderData[0].order?.woUrl) {
                          event.preventDefault();
                        }
                      }}
                      target={orderData[0].order?.woUrl ? '_blank' : undefined}
                      rel={orderData[0].order?.woUrl ? 'noreferrer' : undefined}
                      style={{
                        cursor: orderData[0].order?.woUrl ? 'pointer' : 'not-allowed',
                        textDecoration: 'none'
                      }}
                      title={orderData[0].order?.woUrl ? `Open WO folder: ${orderData[0].order.woUrl}` : 'No WO folder link available'}
                    >
                      <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: orderData[0].order?.woUrl ? '#ff8c00' : '#cccccc' }}></i>
                    </a>
                  )}
                </div>
                </div>

                <div className="order-popup-info-segment order-popup-info-po">
                  <div className="order-popup-info-field-line">
                    <span className="order-popup-info-field-label">
                      <b>PO Num</b>:
                    </span>{' '}
                    <span className="order-popup-info-field-value">{orderData[0].order.custponum}</span>
                  </div>
                </div>

                <div className="order-popup-info-segment order-popup-info-order-status-col" ref={orderStatusDropdownRef}>
                <div className="order-popup-field-label"><b>Order Status</b></div>
                <div className="order-popup-order-status" style={{ position: 'relative', display: 'flex', justifyContent: 'flex-start', minWidth: 0, width: '100%' }}>
                  <div className="d-flex align-items-center gap-1" style={{ width: '100%', minWidth: 0 }}>
                    <div
                      ref={orderStatusBoxRef}
                      className="form-control order-popup-status-box d-flex align-items-start"
                      style={{
                        cursor: "pointer",
                        backgroundColor: "white",
                        border: "1px solid #ced4da",
                        padding: "2px 6px",
                        fontSize: "0.8rem",
                        width: "100%",
                        maxWidth: "100%"
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={toggleOrderStatusDropdown}
                    >
                      <div className="order-popup-status-tags-inner d-flex flex-wrap gap-1 align-items-center" style={{ flex: 1, minWidth: 0 }}>
                        {/* Current selections */}
                        {(orderStatusSelections[firstLineId] || []).map((tag, index) => (
                          <span
                            key={`current-${index}`}
                            className="badge"
                            style={{
                              backgroundColor: tag.color,
                              color: "white",
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "3px",
                              lineHeight: "1.2",
                              fontWeight: 600
                            }}
                          >
                            {tag.icon && <i className={tag.icon} aria-hidden="true" style={{ fontSize: '0.65rem' }}></i>}
                            {tag.label}
                          </span>
                        ))}

                        {/* Submitted tags with click to deselect */}
                        {(submittedOrderStatuses[firstLineId] || []).map((tag, index) => {
                          const isMarkedForDeselection = (tagsToDeselect[firstLineId] || []).some(t => t.label === tag.label);
                          return (
                            <span
                              key={`submitted-${index}`}
                              className="badge"
                              style={{
                                backgroundColor: isMarkedForDeselection ? "#6c757d" : tag.color,
                                color: "white",
                                fontSize: "0.7rem",
                                padding: "2px 8px",
                                borderRadius: "10px",
                                cursor: "pointer",
                                opacity: isMarkedForDeselection ? 0.6 : 1,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                                lineHeight: "1.2",
                                fontWeight: 600
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOrderStatusTagDeselect(firstLineId, tag);
                              }}
                              title={isMarkedForDeselection ? "Click to keep this tag" : "Click to deselect this tag"}
                            >
                              {tag.icon && <i className={tag.icon} aria-hidden="true" style={{ fontSize: '0.65rem' }}></i>}
                              {tag.label}
                            </span>
                          );
                        })}

                        {(orderStatusSelections[firstLineId] || []).length === 0 && (submittedOrderStatuses[firstLineId] || []).length === 0 && (
                          <span className="text-muted" style={{ fontSize: '0.75rem' }}>Select tags...</span>
                        )}
                      </div>
                    </div>
                    {hasOrderStatusChangesToSubmit(firstLineId) && (
                      <button
                        type="button"
                        className="btn btn-success btn-sm order-status-submit-btn"
                        style={{
                          minWidth: "18px",
                          height: "18px",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "9px",
                          borderRadius: "2px",
                          flexShrink: 0
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOrderStatusSubmit(firstLineId);
                        }}
                        title="Submit changes"
                      >
                        ✓
                      </button>
                    )}
                  </div>
                  
                </div>
                </div>

                <div className="order-popup-info-shipping-country-group">
                <div className="order-popup-info-segment order-popup-info-shipping-method">
                  <div className="order-popup-field-label"><b>Shipping Method</b></div>
                  <div className="order-popup-info-field-value order-popup-info-header-value">
                    {renderShippingMethodLink(headerShippingMethod)}
                  </div>
                </div>

                <div className="order-popup-info-segment order-popup-info-country">
                  <div className="order-popup-field-label"><b>Country</b></div>
                  <div className="order-popup-info-field-value order-popup-info-header-value">
                    {headerIsInternational ? (
                      <span className="order-popup-country-intl">{headerCountryName}</span>
                    ) : (
                      headerCountryName
                    )}
                  </div>
                </div>
                </div>

                <div className="order-popup-info-segment order-popup-info-availability">
                  <div className="order-popup-field-label"><b>Availability Status</b></div>
                  <div className="order-popup-info-field-value order-popup-info-header-value">
                    {headerAvailabilityReport ? (
                      <BomAvailabilityPill
                        missing={headerAvailabilityReport.missingPartsCount}
                        total={headerAvailabilityReport.totalPartsCount}
                      />
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </div>
                </div>
                </div>

                <div className="order-popup-info-secondary">
                <div className="order-popup-info-comments-access">
                {/* Chat — label and chat icon on one row, above Order Access */}
                <div className="order-popup-info-comments">
                  <div className="order-popup-field-label"><b>Chat</b></div>
                  <HoverPO
                    hideBadge
                    orderLineId={firstLineId}
                    comments={aggregatedComments}
                    orderNumber={orderData[0]?.order?.ordernum}
                    customerName={orderData[0]?.order?.customer?.name}
                    customerId={orderData[0]?.order?.customer?.id}
                    sourcePage={commentSourcePage}
                    onDeleteComment={handleModalDeleteComment}
                    onResolveThread={handleModalResolveThread}
                    onAddComment={handleModalAddComment}
                  >
                    <OrderCommentsButton
                      total={aggregatedComments.length}
                      unread={commentUnread}
                      presenceUsers={commentWatchers}
                      active={commentChatOpen || commentWatchers.length > 0}
                    />
                  </HoverPO>
                </div>
                
                {/* Order Access — same links as the table column */}
                <div className="order-popup-info-order-access">
                  <div className="order-popup-field-label"><b>Order Access</b></div>
                  <div className="order-popup-info-order-access-chips">
                    {headerOrderUrls.length > 0 ? (
                      headerOrderUrls.map((urlItem) => (
                        <div key={urlItem.id} className="order-popup-info-order-access-chip">
                          <a
                            href={urlItem.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`${urlItem.urlName}: ${urlItem.url}`}
                          >
                            {urlItem.urlName}
                          </a>
                          {typeof onDeleteOrderAccessLink === 'function' && (
                            <button
                              type="button"
                              onClick={() => onDeleteOrderAccessLink(
                                orderAccessLine?.id,
                                urlItem.id,
                                orderAccessLine?.order?.ordernum || orderData?.[0]?.order?.ordernum,
                                urlItem.urlName
                              )}
                              title="Delete this URL"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <span className="text-muted" style={{ fontSize: '0.75rem' }}>No links</span>
                    )}
                    {typeof onOpenOrderAccess === 'function' && orderAccessLine && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm order-popup-info-order-access-add"
                        onClick={() => onOpenOrderAccess(orderAccessLine)}
                        title="Add folder access link"
                      >
                        <i className="fa fa-plus" style={{ marginRight: '3px' }}></i>
                        Add
                      </button>
                    )}
                  </div>
                </div>
                </div>

                {/* Eng drawing + Build part — right side */}
                <div className="order-popup-info-actions">
                  <a
                    href="https://aeroplicity.com/file-share/items/Departments/Engineering/Drawings?view=all"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-sm"
                    style={{
                      fontSize: '0.875rem',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                    title="Engineering drawings (Aeroplicity)"
                  >
                    Eng drawing
                  </a>
                  <a
                    href="https://gigaflightinc.cetecerp.com/widget/generic?name=enter_build_for_stock_order"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-sm"
                    style={{
                      fontSize: '0.875rem',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                    title="Open Build Part"
                  >
                    Build part
                  </a>
                </div>
                </div>
              </div>
            </div>

            {/* Microsoft-style Tab Header */}
            <div className="d-flex justify-content-between align-items-center border-bottom bg-white order-popup-tab-bar" style={{ flexShrink: 0, paddingRight: '28px', paddingLeft: '12px' }}>
              <div className="d-flex">
                <button
                  className={`px-4 py-3 border-0 bg-transparent ${activeView === 'orderLines' ? 'border-bottom border-primary text-primary fw-bold' : 'text-muted'}`}
                  onClick={() => handleViewChange('orderLines')}
                  style={{
                    borderBottom: activeView === 'orderLines' ? '3px solid #0078d4' : '3px solid transparent'
                  }}
                >
                  Order Lines
                </button>
                <button
                  className={`px-4 py-3 border-0 bg-transparent ${activeView === 'bomParts' ? 'border-bottom border-primary text-primary fw-bold' : 'text-muted'}`}
                  onClick={() => handleViewChange('bomParts')}
                  style={{
                    borderBottom: activeView === 'bomParts' ? '3px solid #0078d4' : '3px solid transparent'
                  }}
                >
                  BOM Parts ({bomPartsCount})
                </button>
              </div>

              {/* View Toggle Buttons (Microsoft-style) */}
              <div className="px-3 d-flex align-items-center gap-2 order-popup-tab-actions" style={{ flexWrap: 'nowrap', flexShrink: 0, marginRight: '0' }}>
                <Button
                  variant="outline-success"
                  size="sm"
                  className="d-flex align-items-center gap-1"
                  onClick={handleExportOrderLines}
                  title="Export Order Lines to Excel"
                  style={{ fontSize: '0.8rem', padding: '5px 10px' }}
                >
                  <i className="fa fa-file-excel-o" aria-hidden="true"></i>
                  Export Lines
                </Button>
                <Button
                  variant="outline-success"
                  size="sm"
                  className="d-flex align-items-center gap-1"
                  onClick={handleExportBomParts}
                  title="Export BOM Parts to Excel"
                  style={{ fontSize: '0.8rem', padding: '5px 10px' }}
                >
                  <i className="fa fa-file-excel-o" aria-hidden="true"></i>
                  Export BOM
                </Button>
                {/* Switch View Button with Dual Screen Symbol */}
                <button
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
                  onClick={handleSwitchView}
                  style={{
                    fontSize: '0.875rem',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #d1d5db',
                    backgroundColor: '#f9fafb'
                  }}
                >
                  <div className="d-flex gap-1">
                    <div
                      style={{
                        width: '8px',
                        height: '12px',
                        backgroundColor: activeView === 'orderLines' ? '#0078d4' : '#dee2e6',
                        borderRadius: '1px',
                        border: '1px solid #adb5bd'
                      }}
                    ></div>
                    <div
                      style={{
                        width: '8px',
                        height: '12px',
                        backgroundColor: activeView === 'bomParts' ? '#0078d4' : '#dee2e6',
                        borderRadius: '1px',
                        border: '1px solid #adb5bd'
                      }}
                    ></div>
                  </div>
                  Switch View
                </button>

                {/* Dual View Button */}
                <button
                  className={`btn btn-sm d-flex align-items-center gap-2 ${activeView === 'both' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={handleDualView}
                  style={{
                    fontSize: '0.875rem',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  title="View Both Tables"
                >
                  {/* Creative Dual View Symbol */}
                  <div style={{
                    position: 'relative',
                    width: '20px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {/* Left Panel */}
                    <div style={{
                      position: 'absolute',
                      left: '0',
                      top: '2px',
                      width: '8px',
                      height: '12px',
                      backgroundColor: activeView === 'both' ? '#ffffff' : '#0078d4',
                      borderRadius: '2px',
                      border: '1px solid #0078d4',
                      transform: 'skew(-5deg)',
                      boxShadow: activeView === 'both' ? '0 2px 4px rgba(0,0,0,0.2)' : 'none'
                    }}></div>

                    {/* Right Panel */}
                    <div style={{
                      position: 'absolute',
                      right: '0',
                      top: '2px',
                      width: '8px',
                      height: '12px',
                      backgroundColor: activeView === 'both' ? '#ffffff' : '#0078d4',
                      borderRadius: '2px',
                      border: '1px solid #0078d4',
                      transform: 'skew(5deg)',
                      boxShadow: activeView === 'both' ? '0 2px 4px rgba(0,0,0,0.2)' : 'none'
                    }}></div>

                    {/* Center Divider */}
                    <div style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '1px',
                      height: '10px',
                      backgroundColor: activeView === 'both' ? '#0078d4' : 'transparent'
                    }}></div>

                    {/* Connection Lines */}
                    <div style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: activeView === 'both' ? '#0078d4' : 'transparent',
                      opacity: activeView === 'both' ? 1 : 0
                    }}></div>
                  </div>

                  Dual View
                </button>
              </div>
            </div>

            {/* Content based on active view */}
            <div className={`order-popup-workspace ${activeView === 'both' ? 'is-dual' : 'is-single'}`}>
            <div className="p-3 order-popup-body-content order-popup-workspace-main" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, height: '100%' }}>
              {activeView === 'orderLines' ? (
                <div className="order-popup-lines-pane">
                  <h6 className="mb-3 text-muted" style={{ flexShrink: 0 }}>Order Lines</h6>
                  <div className="table-responsive order-popup-table-scroll">
                    <table className="table table-hover order-popup-lines-table order-popup-lines-compact">
                      <thead className="table-light">
                        <tr className="text-center">
                          <th style={{ cursor: 'pointer' }} onClick={() => handleOrderLinesSort('line')}>Line{getOrderLinesSortIcon('line')}</th>
                          <th className="order-popup-part-th" style={{ cursor: 'pointer' }} onClick={() => handleOrderLinesSort('part')}>Part{getOrderLinesSortIcon('part')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleOrderLinesSort('shipDate')}>Ship Date{getOrderLinesSortIcon('shipDate')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleOrderLinesSort('shipQty')}>Ship Qty{getOrderLinesSortIcon('shipQty')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleOrderLinesSort('qoh')}>QOH{getOrderLinesSortIcon('qoh')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleOrderLinesSort('qoo')}>QOO{getOrderLinesSortIcon('qoo')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleOrderLinesSort('qr')}>QR{getOrderLinesSortIcon('qr')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleOrderLinesSort('qtyAvail')}>Qty Avail{getOrderLinesSortIcon('qtyAvail')}</th>
                          <th className="order-popup-line-status-th">
                            <div className="order-popup-line-status-header">
                              <span className="order-popup-th-label">Line Status</span>
                              
                              {/* Bulk mode checkbox */}
                              <div className="form-check order-popup-bulk-check mb-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="bulkModeCheckbox"
                                  checked={bulkModeEnabled}
                                  onChange={(e) => {
                                    setBulkModeEnabled(e.target.checked);
                                    if (!e.target.checked) {
                                      setHasBulkChanges(false);
                                    }
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    width: '16px',
                                    height: '16px'
                                  }}
                                  title="Enable bulk mode to apply status changes to all lines"
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="bulkModeCheckbox"
                                  style={{
                                    cursor: 'pointer',
                                    color: bulkModeEnabled ? '#90ee90' : 'rgba(255,255,255,0.9)',
                                    fontWeight: bulkModeEnabled ? 'bold' : 'normal'
                                  }}
                                >
                                  Bulk
                                </label>
                              </div>
                              
                              {/* Bulk submit button - only shows when bulk mode is on and there are changes */}
                              {bulkModeEnabled && hasBulkChanges && (
                                <button
                                  type="button"
                                  className="btn btn-success btn-sm"
                                  style={{
                                    minWidth: "60px",
                                    height: "24px",
                                    padding: "0 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "11px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontWeight: 'bold'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBulkSubmit();
                                  }}
                                  title="Submit all bulk status changes"
                                >
                                  Save All
                                </button>
                              )}
                              
                              <button
                                ref={bulkLineStatusTriggerRef}
                                type="button"
                                className="btn btn-sm p-0"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: '2px 4px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  color: '#007bff'
                                }}
                                onClick={toggleBulkLineStatusDropdown}
                                title="Select tag to apply to all lines"
                              >
                                ⚡
                              </button>
                              {pendingBulkTag && (
                                <button
                                  type="button"
                                  className="btn btn-success btn-sm"
                                  style={{
                                    minWidth: "18px",
                                    height: "18px",
                                    padding: "0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "10px",
                                    borderRadius: "2px",
                                    cursor: "pointer"
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBulkLineStatusConfirm();
                                  }}
                                  title={`Confirm applying "${pendingBulkTag.label}" to all lines`}
                                >
                                  ✓
                                </button>
                              )}
                            </div>
                          </th>
                          <th className="order-popup-action-th">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedOrderData.map((item, index) => {
                          const qoh = Number(item.part?.qoh_cache) || 0;
                          const qoo = Number(item.part?.qoo_cache) || 0;
                          const qr = Number(item.part?.qr_cache) || 0;
                          const qtyInHand = qoh - qr;
                          const isExpanded = expandedRows[index];
                          const bomParts = item?.bom?.bomParts || [];

                          const isGreenRow = shouldRowBeGreen(item);

                          return (
                            <React.Fragment key={item.id != null ? item.id : index}>
                              <tr
                                className={`text-center${isGreenRow ? ' order-popup-line-ready' : ''}`}
                                style={isGreenRow ? { backgroundColor: '#d4edda' } : undefined}
                              >
                                <td>{item.lineitem}</td>
                                <td className="order-popup-part-td">
                                  {/* This wrapper ensures content is centered within the cell */}
                                  <div style={dualViewStyles.orderLinePartCell}>
                                    <span>
                                      {(() => {
                                        const partNumber = String(item.prcpart || '');
                                        const result = partNumber.length >= 3 ? partNumber.substring(3) : partNumber || 'N/A';
                                        return result;
                                      })()}
                                    </span>

                                    {/* Cool link icon with modern design */}
                                    <div className="dropdown d-inline-block ms-4">
                                      <button
                                        className="btn btn-outline-primary btn-sm part-link-trigger"
                                        type="button"
                                        // ✅ Call the custom handler, passing the button element for position calculation
                                        onClick={(e) => handleDropdownToggle(item.lineitem, e.currentTarget, item.part?.id)}
                                        aria-expanded={openDropdownData.id === item.lineitem}
                                        title="View Part Details"
                                        style={{
                                          padding: '4px 8px',
                                          fontSize: '0.75rem',
                                          minWidth: '28px',
                                          minHeight: '28px',
                                          borderRadius: '6px',
                                          border: '2px solid #0078d4',
                                          backgroundColor: '#f8f9fa'
                                        }}>
                                        <i className="fa fa-link" aria-hidden="true" style={{ fontSize: '0.8rem', color: '#0078d4' }}></i>
                                      </button>
                                    </div>
                                  </div>
                                </td>
                                <td>{item.promisedate || item.promisedate}</td>
                                <td>{formatQtyDisplay(item.balancedue)}</td>
                                <td>{qoh}</td>
                                <td>
                                  <div className="d-flex align-items-center justify-content-center">
                                    <span>{qoo}</span>
                                    {qoo > 0 && (
                                      <a href={`https://gigaflightinc.cetecerp.com/part/${item.part?.id}/pos`} target="_blank" rel="noreferrer" title="purchase orders" className="ms-2">
                                        <i className="fa fa-shopping-cart text-primary" aria-hidden="true"></i>
                                      </a>
                                    )}
                                  </div>
                                </td>
                                <td>{qr}</td>
                                <td>
                                  <span className={qtyInHand < 0 ? 'bg-danger text-white px-2 py-1 rounded' : ''}>
                                    {qtyInHand}
                                  </span>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-2 order-popup-line-status-row">
                                    <div className="line-status-dropdown position-relative flex-grow-1" style={{ minWidth: 0 }}>
                                      <div
                                        ref={(el) => { lineStatusTriggerRefs.current[`${item.id}`] = el; }}
                                        className="form-control d-flex align-items-center order-popup-line-status-box"
                                        style={{
                                          cursor: 'pointer',
                                          backgroundColor: 'white',
                                          border: '1px solid #ced4da',
                                          gap: '6px'
                                        }}
                                        onClick={() => toggleLineStatusDropdown(`${item.id}`)}
                                      >
                                        <div
                                          className="d-flex flex-wrap gap-1 order-popup-line-status-tags"
                                          style={{
                                            flex: 1,
                                            minWidth: 0,
                                            justifyContent: (lineStatusSelections[`${item.id}`] || []).length === 0 &&
                                              (submittedLineStatuses[`${item.id}`] || []).length === 0
                                              ? 'center'
                                              : 'flex-start'
                                          }}
                                        >
                                          {/* Pending selections (not yet submitted) */}
                                          {(lineStatusSelections[`${item.id}`] || [])
                                            .filter((tag) => !(submittedLineStatuses[`${item.id}`] || []).some((s) => s.label === tag.label))
                                            .map((tag, tagIndex) => (
                                            <span
                                              key={tagIndex}
                                              className="badge"
                                              style={{
                                                backgroundColor: tag.color,
                                                color: 'white',
                                                fontSize: '0.875rem',
                                                padding: '5px 11px',
                                                borderRadius: '12px',
                                                fontWeight: 600,
                                                lineHeight: '1.3'
                                              }}
                                            >
                                              {tag.label}
                                              <button
                                                type="button"
                                                style={{
                                                  background: 'none',
                                                  border: 'none',
                                                  color: 'white',
                                                  fontSize: '12px',
                                                  fontWeight: 'bold',
                                                  padding: '0',
                                                  margin: '0 0 0 4px',
                                                  width: '14px',
                                                  height: '14px',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  cursor: 'pointer',
                                                  lineHeight: '1'
                                                }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleLineStatusToggle(`${item.id}`, tag);
                                                }}
                                              >
                                                ×
                                              </button>
                                            </span>
                                          ))}

                                          {/* Saved tags — click to deselect before submit */}
                                          {(submittedLineStatuses[`${item.id}`] || []).map((tag, tagIndex) => {
                                            const isMarkedForDeselection = (lineTagsToDeselect[`${item.id}`] || []).some(t => t.label === tag.label);
                                            return (
                                              <span
                                                key={`submitted-${tagIndex}`}
                                                className={`badge${isMarkedForDeselection ? ' line-status-tag-pending-removal' : ''}`}
                                                style={{
                                                  backgroundColor: isMarkedForDeselection ? "#9ca3af" : tag.color,
                                                  color: "white",
                                                  fontSize: "0.875rem",
                                                  padding: "5px 11px",
                                                  borderRadius: "12px",
                                                  cursor: "pointer",
                                                  opacity: 1,
                                                  fontWeight: 600,
                                                  lineHeight: "1.3"
                                                }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleLineTagDeselect(`${item.id}`, tag);
                                                }}
                                                title={isMarkedForDeselection ? "Pending removal — click to keep, or Save to remove" : "Click to mark for removal (Save to confirm)"}
                                              >
                                                {formatLineStatusLabel(tag)}
                                              </span>
                                            );
                                          })}

                                          {(lineStatusSelections[`${item.id}`] || []).length === 0 &&
                                            (submittedLineStatuses[`${item.id}`] || []).length === 0 && (
                                              <span className="text-muted" style={{ textAlign: 'center' }}>Select tags...</span>
                                            )}
                                        </div>
                                      </div>
                                    </div>
                                    {renderLinePresence?.(item.id) || <LineStatusPresence lineId={item.id} />}

                                    {/* Submit button - only shows when there are changes and bulk mode is OFF */}
                                    {!bulkModeEnabled && hasLineChangesToSubmit(`${item.id}`) && (
                                      <button
                                        type="button"
                                        className="btn btn-success btn-sm line-status-submit-btn"
                                        style={{
                                          minWidth: "20px",
                                          height: "20px",
                                          padding: "0",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontSize: "10px",
                                          borderRadius: "2px"
                                        }}
                                        disabled={Boolean(modalLineStatusLockOwner(item.id))}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleLineStatusSubmit(`${item.id}`);
                                        }}
                                        title={
                                          modalLineStatusLockOwner(item.id)
                                            ? `${modalLineStatusLockOwner(item.id).name} is editing this line status`
                                            : "Submit line status changes"
                                        }
                                      >
                                        ✓
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td className="order-popup-action-td">
                                  <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleRow(index)} title="Show/Hide BOM">
                                    {isExpanded ? <i className="fa-solid fa-caret-up"></i> : <i className="fa-solid fa-caret-down"></i>}
                                  </button>
                                </td>
                              </tr>

                              {isExpanded && (
                                <tr>
                                  <td colSpan="9" className="bg-light">
                                    <div className="px-5 py-2">
                                      <strong>BOM Details:</strong>
                                      {bomParts.length > 0 ? (
                                        <table className="table border rounded mt-2">
                                          <thead className="table-light">
                                            <tr className="text-center">
                                              <th>Part</th>
                                              <th>Qty/Top</th>
                                              <th>QOH</th>
                                              <th>QOO</th>
                                              <th>QR</th>
                                              <th>Qty Avail</th>
                                              <th className="exclude-col">Exclude</th>
                                              <th>Archive</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {sortBomPartsNegativeQtyAvailFirst(bomParts).map((bom, i) => {
                                              const part = bom.part || {};
                                              const qohBom = Number(part.qoh_cache) || 0;
                                              const qooBom = Number(part.qoo_cache) || 0;
                                              const qrBom = Number(part.qr_cache) || 0;
                                              const qtyInHandBom = getBomPartQtyAvail(bom);
                                              const isBomArchived = !!bom.archived;

                                              return (
                                                <tr key={i} style={isBomArchived ? { opacity: 0.5, textDecoration: 'line-through' } : undefined}>
                                                  <td>
                                                    {renderPartLinkTrigger(
                                                      getPartDisplayNumber(bom.prcpart),
                                                      `bom-detail-${item.lineitem}-${i}`,
                                                      bom.part?.id,
                                                      'View Part Details'
                                                    )}
                                                  </td>
                                                  <td>{bom.qty_per_top}</td>
                                                  <td>{qohBom}</td>
                                                  <td>
                                                    <div className="d-flex align-items-center justify-content-center">
                                                      <span>{qooBom}</span>
                                                      {qooBom > 0 && (
                                                        <a href={`https://gigaflightinc.cetecerp.com/part/${part.id}/pos`} target="_blank" rel="noreferrer" title="purchase orders" className="ms-2">
                                                          <i className="fa fa-shopping-cart text-primary" aria-hidden="true"></i>
                                                        </a>
                                                      )}
                                                    </div>
                                                  </td>
                                                  <td>{qrBom}</td>
                                                  <td>
                                                    <span className={qtyInHandBom < 0 ? 'bg-danger text-white px-2 py-1 rounded' : ''}>
                                                      {qtyInHandBom}
                                                    </span>
                                                  </td>
                                                  <td className="exclude-col">
                                                    {renderExcludeCell(bom.prcpart, qtyInHandBom)}
                                                  </td>
                                                  <td>
                                                    {renderBomArchiveCell(bom)}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      ) : (
                                        <em className="text-center">No BOM parts available.</em>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : activeView === 'bomParts' ? (
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <h6 className="mb-3 text-muted" style={{ flexShrink: 0 }}>All BOM Parts (Aggregated)</h6>
                  <div className="table-responsive order-popup-table-scroll" style={{ flex: 1, minHeight: '0' }}>
                    <table className="table table-hover border rounded order-popup-bom-compact">
                      <thead className="table-light">
                        <tr className="text-center">
                          <th style={{ minWidth: '60px' }}>S.No</th>
                          <th style={{ minWidth: '200px', maxWidth: '300px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('topLevel')}>Top level parts{getBomPartsSortIcon('topLevel')}</th>
                          <th style={{ minWidth: '120px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('componentPart')}>Component part{getBomPartsSortIcon('componentPart')}</th>
                          <th style={{ minWidth: '100px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('qtyOrder')}>Qty/Order{getBomPartsSortIcon('qtyOrder')}</th>
                          <th style={{ minWidth: '80px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('qoh')}>QOH{getBomPartsSortIcon('qoh')}</th>
                          <th style={{ minWidth: '80px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('qoo')}>QOO{getBomPartsSortIcon('qoo')}</th>
                          <th style={{ minWidth: '80px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('qr')}>QR{getBomPartsSortIcon('qr')}</th>
                          <th style={{ minWidth: '100px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('qtyAvail')}>Qty Avail{getBomPartsSortIcon('qtyAvail')}</th>
                          <th className="exclude-col" style={{ minWidth: '100px' }}>Exclude</th>
                          <th style={{ minWidth: '100px' }}>Archive</th>
                        </tr>
                      </thead>
                      <tbody className="text-center">
                        {bomPartsCount > 0 ? (
                          sortedAggregatedBomParts.map((line, idx) => (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td style={dualViewStyles.tableCell} className='px-3'>
                                {(() => {
                                  const parentPartsArray = Array.from(line.parentParts);
                                  return (
                                    <div
                                      style={{
                                        ...dualViewStyles.parentPartsCell,
                                        // 1. FLEXBOX: Align text left, button right
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        minHeight: '28px'
                                      }}
                                    >
                                      {/* Left-aligned Content (The Parent Parts List) */}
                                      <div style={{ marginRight: '8px' }}>
                                        {parentPartsArray.map((part, index) => {
                                          const partNumber = part || '';
                                          const displayPart = partNumber.length >= 3 ? partNumber.substring(3) : partNumber || 'N/A';
                                          // Look up part ID from orderData
                                          const parentPartId = parentPartIdMap.get(part) || 
                                            (Array.isArray(orderData) ? orderData.find(item => item?.prcpart === part)?.part?.id : null);
                                          return (
                                            <div key={index} style={{ marginBottom: '4px' }}>
                                              {parentPartsArray.length > 1 ? (
                                                <>
                                                  <span style={{ fontWeight: 'bold', color: '#0078d4' }}>
                                                    {index + 1})
                                                  </span>
                                                  {parentPartId ? (
                                                    <a
                                                      href={`https://gigaflightinc.cetecerp.com/part/${parentPartId}/inventory`}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="ms-2"
                                                      style={{ textDecoration: 'none' }}
                                                    >
                                                      {displayPart}
                                                    </a>
                                                  ) : (
                                                    <span className="ms-2">{displayPart}</span>
                                                  )}
                                                </>
                                              ) : (
                                                parentPartId ? (
                                                  <a
                                                    href={`https://gigaflightinc.cetecerp.com/part/${parentPartId}/inventory`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ textDecoration: 'none' }}
                                                  >
                                                    {displayPart}
                                                  </a>
                                                ) : (
                                                  <span>{displayPart}</span>
                                                )
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>

                                      {/* Right-aligned Content (The Dropdown Button) */}
                                      <div className="dropdown d-inline-block">
                                        <button
                                          // ✅ FIX: ADD THE UNIQUE CLASS NAME HERE
                                          className={`btn btn-outline-primary btn-sm part-link-trigger dropdown-button-line-${line.prcpart}`}
                                          type="button"
                                          // ADDED: Custom handler for state management and positioning
                                          onClick={(e) => handleDropdownToggle(line.prcpart, e.currentTarget, line.part?.id)}
                                          aria-expanded={openDropdownData.id === line.prcpart}
                                          title="View Parent Part Details"
                                          style={{
                                            padding: '4px 8px',
                                            fontSize: '0.75rem',
                                            minWidth: '28px',
                                            minHeight: '28px',
                                            borderRadius: '6px',
                                            border: '2px solid #0078d4',
                                            backgroundColor: '#f8f9fa'
                                          }}
                                        >
                                          <i className="fa fa-link" aria-hidden="true" style={{ fontSize: '0.8rem', color: '#0078d4' }}></i>
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </td>
                              {/* <td style={dualViewStyles.tableCell}>
                                {(() => {
                                  const parentPartsArray = Array.from(line.parentParts);
                                  return (
                                    <div style={dualViewStyles.parentPartsCell}>
                                      {parentPartsArray.map((part, index) => {
                                        const partNumber = part || '';
                                        const displayPart = partNumber.length >= 3 ? partNumber.substring(3) : partNumber || 'N/A';
                                        return (
                                          <div key={index} style={{ marginBottom: '4px' }}>
                                            {parentPartsArray.length > 1 ? (
                                              <>
                                                <span style={{ fontWeight: 'bold', color: '#0078d4' }}>
                                                  {index + 1})
                                                </span>
                                                <span className="ms-2">{displayPart}</span>
                                              </>
                                            ) : (
                                              <span>{displayPart}</span>
                                            )}
                                          </div>
                                        );
                                      })}

                                      
                                      <div className="dropdown d-inline-block" style={dualViewStyles.hyperlinkButton}>
                                        <button className="btn btn-outline-primary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" title="View Parent Part Details" style={{
                                          padding: '4px 8px',
                                          fontSize: '0.75rem',
                                          minWidth: '28px',
                                          minHeight: '28px',
                                          borderRadius: '6px',
                                          border: '2px solid #0078d4',
                                          backgroundColor: '#f8f9fa',
                                          transition: 'all 0.2s ease'
                                        }}>
                                          <i className="fa fa-link" aria-hidden="true" style={{ fontSize: '0.8rem', color: '#0078d4' }}></i>
                                        </button>
                                        <ul className="dropdown-menu dropdown-menu-end" style={{
                                          zIndex: 9999,
                                          position: 'absolute',
                                          minWidth: '220px',
                                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                          border: '1px solid #dee2e6',
                                          borderRadius: '8px',
                                          marginTop: '-5px'
                                        }}>
                                          <li><a className="dropdown-item" href={`https://gigaflightinc.cetecerp.com/part/${line.part?.id}/inventory`} target="_blank" rel="noreferrer">
                                            <i className="fa fa-boxes me-2" aria-hidden="true" style={{ color: '#28a745' }}></i>Inventory
                                          </a></li>
                                          <li><a className="dropdown-item" href={`https://gigaflightinc.cetecerp.com/part/${line.part?.id}/waterfall`} target="_blank" rel="noreferrer">
                                            <i className="fa fa-chart-line me-2" aria-hidden="true" style={{ color: '#ffc107' }}></i>Waterfall
                                          </a></li>
                                          <li><a className="dropdown-item" href={`https://gigaflightinc.cetecerp.com/part/${line.part?.id}/pos`} target="_blank" rel="noreferrer">
                                            <i className="fa fa-shopping-cart me-2" aria-hidden="true" style={{ color: '#007bff' }}></i>Purchase Orders
                                          </a></li>
                                        </ul>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </td> */}
                              <td>
                                {renderPartLinkTrigger(
                                  getPartDisplayNumber(line.prcpart),
                                  getBomComponentDropdownId(line.prcpart),
                                  line.part?.id,
                                  'View Component Part Details'
                                )}
                              </td>
                              <td>{formatQtyDisplay(line.qty_order)}</td>
                              <td>{line.qohBom}</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center">
                                  <span>{line.qooBom}</span>
                                  {line.qooBom > 0 && (
                                    <a href={`https://gigaflightinc.cetecerp.com/part/${line.part?.id}/pos`} target="_blank" rel="noreferrer" title="purchase orders" className="ms-2">
                                      <i className="fa fa-shopping-cart text-primary" aria-hidden="true"></i>
                                    </a>
                                  )}
                                </div>
                              </td>
                              <td>{line.qrBom}</td>
                              <td>
                                <span className={line.qtyAvail < 0 ? 'bg-danger text-white px-2 py-1 rounded' : ''}>
                                  {line.qtyAvail}
                                </span>
                              </td>
                              <td className="exclude-col">
                                {renderExcludeCell(line.prcpart, line.qtyAvail)}
                              </td>
                              <td>
                                {renderAggregatedArchiveCell(line)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="10" className="text-center py-5">
                              <div className="text-muted">
                                <i className="fa fa-info-circle me-2"></i>
                                No BOM parts available for this order
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                // Dual View - Creative Split Screen Layout with Optimized Performance
                <div className="position-relative" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* Left Panel - Order Lines */}
                  <div className="order-popup-dual-row">
                    <div className="order-popup-dual-col">
                      <div className="position-relative order-popup-dual-stack" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* Panel Header with Creative Design */}
                        <div className="d-flex align-items-center" style={{ ...dualViewStyles.orderLinesHeader, flexShrink: 0 }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#0078d4',
                            marginRight: '12px',
                            boxShadow: '0 0 8px rgba(0, 120, 212, 0.5)'
                          }}></div>
                          <h6 className="mb-0 text-primary fw-bold">Order Lines</h6>
                          <div className="ms-auto">
                            <span className="badge bg-primary rounded-pill">{orderData.length}</span>
                          </div>
                        </div>

                        <div className="table-responsive order-popup-table-scroll order-popup-dual-view-lines-scroll" style={{ ...dualViewStyles.orderLinesTable, flex: 1, minHeight: '0' }}>
                          <table className="table table-hover mb-0 order-popup-lines-table order-popup-dual-view-lines-table order-popup-dual-lines-compact">
                            <thead className="table-primary">
                              <tr className="text-center">
                                <th style={{ borderTop: 'none', cursor: 'pointer' }} onClick={() => handleOrderLinesSort('line')}>Line{getOrderLinesSortIcon('line')}</th>
                                <th className="order-popup-part-th" style={{ borderTop: 'none', cursor: 'pointer' }} onClick={() => handleOrderLinesSort('part')}>Part{getOrderLinesSortIcon('part')}</th>
                                <th style={{ borderTop: 'none', cursor: 'pointer' }} onClick={() => handleOrderLinesSort('shipDate')}>Ship Date{getOrderLinesSortIcon('shipDate')}</th>
                                <th style={{ borderTop: 'none', cursor: 'pointer' }} onClick={() => handleOrderLinesSort('shipQty')}>Ship Qty{getOrderLinesSortIcon('shipQty')}</th>
                                <th style={{ borderTop: 'none', cursor: 'pointer' }} onClick={() => handleOrderLinesSort('qoh')}>QOH{getOrderLinesSortIcon('qoh')}</th>
                                <th style={{ borderTop: 'none', cursor: 'pointer' }} onClick={() => handleOrderLinesSort('qoo')}>QOO{getOrderLinesSortIcon('qoo')}</th>
                                <th style={{ borderTop: 'none', cursor: 'pointer' }} onClick={() => handleOrderLinesSort('qr')}>QR{getOrderLinesSortIcon('qr')}</th>
                                <th style={{ borderTop: 'none', cursor: 'pointer' }} onClick={() => handleOrderLinesSort('qtyAvail')}>Qty Avail{getOrderLinesSortIcon('qtyAvail')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedOrderData.map((item, index) => {
                                const qoh = Number(item.part?.qoh_cache) || 0;
                                const qoo = Number(item.part?.qoo_cache) || 0;
                                const qr = Number(item.part?.qr_cache) || 0;
                                const qtyInHand = qoh - qr;

                                const isGreenRow = shouldRowBeGreen(item);
                                const rowBackgroundColor = isGreenRow ? '#d4edda' : dualViewStyles.rowStyle?.backgroundColor;

                                return (
                                  <tr
                                    key={item.id != null ? item.id : index}
                                    className={`text-center${isGreenRow ? ' order-popup-line-ready' : ''}`}
                                    style={{
                                      ...dualViewStyles.rowStyle,
                                      backgroundColor: rowBackgroundColor,
                                      // CRITICAL FIX: Elevate the row when the dropdown is open
                                      position: openDropdownData.id === item.lineitem ? 'relative' : 'static',
                                      zIndex: openDropdownData.id === item.lineitem ? 1000 : 1,
                                    }}
                                    onMouseEnter={(e) => handleRowHover(e, true, 'orderLines', shouldRowBeGreen(item))}
                                    onMouseLeave={(e) => handleRowHover(e, false, 'orderLines', shouldRowBeGreen(item))}
                                  >
                                    <td className="fw-bold">{item.lineitem}</td>
                                    <td className="order-popup-part-td px-3">
                                      {/* 1. ADD Flexbox classes to the wrapper:
                                        - d-flex: Enable flex container
                                        - justify-content-between: Push children to opposite ends
                                        - align-items-center: Vertically center items 
                                    */}
                                      <div
                                        className="d-flex justify-content-between align-items-center"
                                        style={dualViewStyles.orderLinePartCell}
                                      >
                                        {/* LEFT-ALIGNED CONTENT (The Part Number) */}
                                        {/* 2. Wrap the text span in a div so it acts as one Flex item */}
                                        <div>
                                          <span className="me-2">
                                            {(() => {
                                              const partNumber = String(item.prcpart || '');
                                              const result = partNumber.length >= 3 ? partNumber.substring(3) : partNumber || 'N/A';
                                              return result;
                                            })()}
                                          </span>
                                        </div>

                                        {/* RIGHT-ALIGNED CONTENT (The Dropdown Button) */}
                                        <div className="dropdown d-inline-block">
                                          <button
                                            className="btn btn-outline-primary btn-sm part-link-trigger"
                                            type="button"
                                            // Call the custom handler, passing the button element for position calculation
                                            onClick={(e) => handleDropdownToggle(item.lineitem, e.currentTarget, item.part?.id)}
                                            aria-expanded={openDropdownData.id === item.lineitem}
                                            title="View Part Details"
                                            style={{
                                              padding: '4px 8px',
                                              fontSize: '0.75rem',
                                              minWidth: '28px',
                                              minHeight: '28px',
                                              borderRadius: '6px',
                                              border: '2px solid #0078d4',
                                              backgroundColor: '#f8f9fa'
                                            }}
                                          >
                                            <i className="fa fa-link" aria-hidden="true" style={{ fontSize: '0.8rem', color: '#0078d4' }}></i>
                                          </button>
                                        </div>
                                      </div>
                                    </td>
                                    <td>{item.promisedate || item.promisedate}</td>
                                    <td className="fw-bold">{formatQtyDisplay(item.balancedue)}</td>
                                    <td>{qoh}</td>
                                    <td>
                                      <div className="d-flex align-items-center justify-content-center">
                                        <span>{qoo}</span>
                                        {qoo > 0 && (
                                          <a href={`https://gigaflightinc.cetecerp.com/part/${item.part?.id}/pos`} target="_blank" rel="noreferrer" title="purchase orders" className="ms-2">
                                            <i className="fa fa-shopping-cart text-primary" aria-hidden="true"></i>
                                          </a>
                                        )}
                                      </div>
                                    </td>
                                    <td>{qr}</td>
                                    <td>
                                      <span className={`badge ${qtyInHand < 0 ? 'bg-danger' : 'bg-success'} rounded-pill`}>
                                        {qtyInHand}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Right Panel - BOM Parts */}
                    <div className="order-popup-dual-col">
                      <div className="position-relative order-popup-dual-stack order-popup-dual-stack-bom" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* Panel Header with Creative Design */}
                        <div className="d-flex align-items-center" style={{ ...dualViewStyles.bomPartsHeader, flexShrink: 0 }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#2196f3',
                            marginRight: '12px',
                            boxShadow: '0 0 8px rgba(33, 150, 243, 0.5)'
                          }}></div>
                          <h6 className="mb-0 text-info fw-bold">BOM Parts (Aggregated)</h6>
                          <div className="ms-auto">
                            <span className="badge bg-info rounded-pill">{bomPartsCount}</span>
                          </div>
                        </div>

                        <div className="table-responsive order-popup-table-scroll" style={{ ...dualViewStyles.bomPartsTable, flex: 1, minHeight: '0' }}>
                          <table className="table table-hover mb-0 order-popup-bom-compact">
                            <thead className="table-info">
                              <tr className="text-center">
                                <th style={{ borderTop: 'none', minWidth: '60px' }}>S.No</th>
                                <th style={{ borderTop: 'none', minWidth: '200px', maxWidth: '300px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('topLevel')}>Top level parts{getBomPartsSortIcon('topLevel')}</th>
                                <th style={{ borderTop: 'none', minWidth: '120px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('componentPart')}>Component part{getBomPartsSortIcon('componentPart')}</th>
                                <th style={{ borderTop: 'none', minWidth: '100px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('qtyOrder')}>Qty/Order{getBomPartsSortIcon('qtyOrder')}</th>
                                <th style={{ borderTop: 'none', minWidth: '80px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('qoh')}>QOH{getBomPartsSortIcon('qoh')}</th>
                                <th style={{ borderTop: 'none', minWidth: '80px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('qoo')}>QOO{getBomPartsSortIcon('qoo')}</th>
                                <th style={{ borderTop: 'none', minWidth: '80px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('qr')}>QR{getBomPartsSortIcon('qr')}</th>
                                <th style={{ borderTop: 'none', minWidth: '100px', cursor: 'pointer' }} onClick={() => handleBomPartsSort('qtyAvail')}>Qty Avail{getBomPartsSortIcon('qtyAvail')}</th>
                                <th className="exclude-col" style={{ borderTop: 'none', minWidth: '100px' }}>Exclude</th>
                                <th style={{ borderTop: 'none', minWidth: '100px' }}>Archive</th>
                              </tr>
                            </thead>
                            <tbody className="text-center">
                              {bomPartsCount > 0 ? (
                                sortedAggregatedBomParts.map((line, idx) => {
                                  const bomComponentDropdownId = getBomComponentDropdownId(line.prcpart);
                                  const isBomPartDropdownOpen =
                                    openDropdownData.id === line.prcpart || openDropdownData.id === bomComponentDropdownId;

                                  return (
                                  <tr
                                    key={idx}
                                    style={{
                                      ...dualViewStyles.rowStyle,
                                      // ✅ FIX: Elevate the row when the dropdown is open
                                      position: isBomPartDropdownOpen ? 'relative' : 'static',
                                      zIndex: isBomPartDropdownOpen ? 1000 : 1, // Set zIndex high enough
                                    }}
                                    onMouseEnter={(e) => handleRowHover(e, true, 'bomParts')}
                                    onMouseLeave={(e) => handleRowHover(e, false, 'bomParts')}
                                  >
                                    <td>{idx + 1}</td>

                                    {/* <td style={dualViewStyles.tableCell}>
                                      {(() => {
                                        const parentPartsArray = Array.from(line.parentParts);
                                        return (
                                          <div style={dualViewStyles.parentPartsCell}>
                                            {parentPartsArray.map((part, index) => {
                                              const partNumber = part || '';
                                              const displayPart = partNumber.length >= 3 ? partNumber.substring(3) : partNumber || 'N/A';
                                              return (
                                                <div key={index} style={{ marginBottom: '4px' }}>
                                                  {parentPartsArray.length > 1 ? (
                                                    <>
                                                      <span style={{ fontWeight: 'bold', color: '#0078d4' }}>
                                                        {index + 1})
                                                      </span>
                                                      <span className="ms-2">{displayPart}</span>
                                                    </>
                                                  ) : (
                                                    <span>{displayPart}</span>
                                                  )}
                                                </div>
                                              );
                                            })}
                                            
                                            <div className="dropdown d-inline-block" style={dualViewStyles.hyperlinkButton}>
                                              <button className="btn btn-outline-primary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" title="View Parent Part Details" style={{
                                                padding: '4px 8px',
                                                fontSize: '0.75rem',
                                                minWidth: '28px',
                                                minHeight: '28px',
                                                borderRadius: '6px',
                                                border: '2px solid #0078d4',
                                                backgroundColor: '#f8f9fa',
                                                transition: 'all 0.2s ease'
                                              }}>
                                                <i className="fa fa-link" aria-hidden="true" style={{ fontSize: '0.8rem', color: '#0078d4' }}></i>
                                              </button>
                                              <ul className="dropdown-menu dropdown-menu-end" style={{
                                                zIndex: 9999,
                                                position: 'absolute',
                                                minWidth: '220px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                border: '1px solid #dee2e6',
                                                borderRadius: '8px',
                                                marginTop: '-5px'
                                              }}>
                                                <li><a className="dropdown-item" href={`https://gigaflightinc.cetecerp.com/part/${line.part?.id}/inventory`} target="_blank" rel="noreferrer">
                                                  <i className="fa fa-boxes me-2" aria-hidden="true" style={{ color: '#28a745' }}></i>Inventory
                                                </a></li>
                                                <li><a className="dropdown-item" href={`https://gigaflightinc.cetecerp.com/part/${line.part?.id}/waterfall`} target="_blank" rel="noreferrer">
                                                  <i className="fa fa-chart-line me-2" aria-hidden="true" style={{ color: '#ffc107' }}></i>Waterfall
                                                </a></li>
                                                <li><a className="dropdown-item" href={`https://gigaflightinc.cetecerp.com/part/${line.part?.id}/pos`} target="_blank" rel="noreferrer">
                                                  <i className="fa fa-shopping-cart me-2" aria-hidden="true" style={{ color: '#007bff' }}></i>Purchase Orders
                                                </a></li>
                                              </ul>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </td> */}

                                    <td className='px-3'>
                                      {(() => {
                                        const parentPartsArray = Array.from(line.parentParts);
                                        return (
                                          <div
                                            style={{
                                              ...dualViewStyles.parentPartsCell,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              minHeight: '28px'
                                            }}
                                          >
                                            {/* Left-aligned Content (The Parent Parts List) */}
                                            <div style={{ marginRight: '8px' }}>
                                              {parentPartsArray.map((part, index) => {
                                                const partNumber = part || '';
                                                const displayPart = partNumber.length >= 3 ? partNumber.substring(3) : partNumber || 'N/A';
                                                // Look up part ID from orderData
                                                const parentPartId = parentPartIdMap.get(part) || 
                                                  (Array.isArray(orderData) ? orderData.find(item => item?.prcpart === part)?.part?.id : null);
                                                return (
                                                  <div key={index} style={{ marginBottom: '4px' }}>
                                                    {parentPartsArray.length > 1 ? (
                                                      <>
                                                        <span style={{ fontWeight: 'bold', color: '#0078d4' }}>
                                                          {index + 1})
                                                        </span>
                                                        {parentPartId ? (
                                                          <a
                                                            href={`https://gigaflightinc.cetecerp.com/part/${parentPartId}/inventory`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="ms-2"
                                                            style={{ textDecoration: 'none', color: '#0078d4', cursor: 'pointer' }}
                                                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                          >
                                                            {displayPart}
                                                          </a>
                                                        ) : (
                                                          <span className="ms-2">{displayPart}</span>
                                                        )}
                                                      </>
                                                    ) : (
                                                      parentPartId ? (
                                                        <a
                                                          href={`https://gigaflightinc.cetecerp.com/part/${parentPartId}/inventory`}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          style={{ textDecoration: 'none' }}
                                                        >
                                                          {displayPart}
                                                        </a>
                                                      ) : (
                                                        <span>{displayPart}</span>
                                                      )
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>

                                            {/* Right-aligned Content (The Dropdown Button) */}
                                            <div className="dropdown d-inline-block">
                                              <button
                                                className={`btn btn-outline-primary btn-sm part-link-trigger dropdown-button-line-${line.prcpart}`}
                                                type="button"
                                                onClick={(e) => handleDropdownToggle(line.prcpart, e.currentTarget, line.part?.id)}
                                                aria-expanded={openDropdownData.id === line.prcpart}
                                                title="View Parent Part Details"
                                                style={{
                                                  padding: '4px 8px',
                                                  fontSize: '0.75rem',
                                                  minWidth: '28px',
                                                  minHeight: '28px',
                                                  borderRadius: '6px',
                                                  border: '2px solid #0078d4',
                                                  backgroundColor: '#f8f9fa'
                                                }}
                                              >
                                                <i className="fa fa-link" aria-hidden="true" style={{ fontSize: '0.8rem', color: '#0078d4' }}></i>
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </td>
                                    <td>
                                      {renderPartLinkTrigger(
                                        getPartDisplayNumber(line.prcpart),
                                        bomComponentDropdownId,
                                        line.part?.id,
                                        'View Component Part Details'
                                      )}
                                    </td>
                                    <td className="fw-bold">{formatQtyDisplay(line.qty_order)}</td>
                                    <td>{line.qohBom}</td>
                                    <td>
                                      <div className="d-flex align-items-center justify-content-center">
                                        <span>{line.qooBom}</span>
                                        {line.qooBom > 0 && (
                                          <a href={`https://gigaflightinc.cetecerp.com/part/${line.part?.id}/pos`} target="_blank" rel="noreferrer" title="purchase orders" className="ms-2">
                                            <i className="fa fa-shopping-cart text-primary" aria-hidden="true"></i>
                                          </a>
                                        )}
                                      </div>
                                    </td>
                                    <td>{line.qrBom}</td>
                                    <td>
                                      <span className={`badge ${line.qtyAvail < 0 ? 'bg-danger' : 'bg-success'} rounded-pill`}>
                                        {line.qtyAvail}
                                      </span>
                                    </td>
                                    <td className="exclude-col">
                                      {renderExcludeCell(line.prcpart, line.qtyAvail)}
                                    </td>
                                    <td>
                                      {renderAggregatedArchiveCell(line)}
                                    </td>
                                  </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan="10" className="text-center py-5">
                                    <div className="text-muted">
                                      <div style={{
                                        fontSize: '48px',
                                        marginBottom: '16px',
                                        opacity: 0.3
                                      }}>
                                        📋
                                      </div>
                                      <div className="fw-bold mb-2">No BOM Parts Available</div>
                                      <div style={{ fontSize: '14px' }}>
                                        This order doesn't have any BOM components
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
              <OrderNotesPanel
                customerName={modalOrderRecord?.customer?.name}
                orderNumber={modalOrderNumber}
                pinned={modalShippingInstructions.forOrder(modalOrderRecord || {})}
                shippingComments={modalOrderRecord?.shipping_instructions || ''}
                notes={modalStickyNotes.notesByOrder[modalOrderNumber] || []}
                customerId={modalShippingInstructions.customerIdFor(modalOrderRecord || {})}
                onAddShipping={modalShippingInstructions.add}
                onUpdateShipping={modalShippingInstructions.update}
                onRemoveShipping={modalShippingInstructions.remove}
                onAdd={modalStickyNotes.add}
                onUpdate={modalStickyNotes.update}
                onRemove={modalStickyNotes.remove}
              />
            </div>
          </div>
        ) : (
          <div className="p-3">
            <p>Loading...</p>
          </div>
        )}
      </Modal.Body>

      {/* Full-screen loading overlay during bulk submit */}
      {isSubmittingBulk && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999999,
            color: 'white'
          }}
        >
          <div className="spinner-border text-light" role="status" style={{ width: '3rem', height: '3rem', marginBottom: '20px' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <h4>Submitting Bulk Status Changes...</h4>
          <p>Please wait, this may take a moment.</p>
          <p style={{ fontSize: '14px', opacity: 0.8 }}>Do not close or refresh the page.</p>
        </div>
      )}
      {renderOrderStatusDropdownMenu()}
      {renderLineStatusDropdownMenus()}
      {renderBulkLineStatusDropdown()}
      {renderPartLinkDropdown()}
    </Modal>
  );
};

// Export with React.memo for performance optimization
export default React.memo(OrderPopup);